import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Popper,
  Select,
  Slider,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import LinkIcon from '@mui/icons-material/Link';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import SyncIcon from '@mui/icons-material/Sync';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import SettingsIcon from '@mui/icons-material/Settings';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import FlipIcon from '@mui/icons-material/Flip';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import Rotate90DegreesCwOutlinedIcon from '@mui/icons-material/Rotate90DegreesCwOutlined';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import { API_BASE_URL } from '../../config';
import {
  approveWordPressPublication,
  fetchWordPressPublishingSettings,
  fetchWordPressRootCategories,
  fetchLegacyRecipeItems,
  MANUAL_DAM_SEARCH_LIMIT,
  fetchLegacyRecipeSuggestions,
  fetchWordPressPublicationItem,
  fetchWordPressPublicationItems,
  fetchWordPressPublishingSummary,
  generateWordPressPublicationImage,
  linkLegacyRecipeToSource,
  reindexLegacyRecipeMatching,
  resetWordPressPublications,
  requestWordPressPublicationUnpublish,
  requestWordPressPublicationSync,
  runWordPressBulkAction,
  saveWordPressPublishingSettings,
  searchLegacyRecipeSourceAssets,
  setWordPressPublicationEnabled,
  WORDPRESS_PUBLISHING_RESET_CONFIRMATION,
} from '../../modules/wordpressPublishing/service';
import QuoteBackgroundsTab from '../../modules/wordpressPublishing/QuoteBackgroundsTab';
import WineryImagesTab from '../../modules/wordpressPublishing/WineryImagesTab';
import FieldCheckSettingsDialog from '../../modules/wordpressPublishing/FieldCheckSettingsDialog';
import {
  FieldCheckBulkPublishDialog,
  FieldCheckCard,
  FieldCheckListChip,
  FieldCheckPublishDialog,
} from '../../modules/wordpressPublishing/FieldCheckPanel';
import {
  formatWordPressFieldCheckMissingLabels,
  getWordPressFieldCheckRequiredMissing,
  isWordPressFieldCheckBlocked,
  summarizeWordPressFieldCheckForItems,
} from '../../modules/wordpressPublishing/fieldCheckUi';
import type { WordPressFieldCheckBulkSummary } from '../../modules/wordpressPublishing/fieldCheckUi';
import MissingInWordPressView from '../../modules/wordpressPublishing/MissingInWordPressView';
import {
  SyncWarningsBulkLine,
  SyncWarningsCard,
  SyncWarningsListChip,
  SyncWarningsPublishNotice,
} from '../../modules/wordpressPublishing/SyncWarningsPanel';
import { countWordPressItemsWithSyncWarnings } from '../../modules/wordpressPublishing/syncWarningsUi';
import type {
  LegacyRecipeListItem,
  LegacyRecipeMatchSuggestion,
  WordPressBulkActionResult,
  WordPressCategoryRootResolution,
  WordPressPublicationItem,
  WordPressPublicationType,
  WordPressPublishingSettingsDetail,
  WordPressPublishingSummary,
  WordPressPublishingTab,
  WordPressRecipeCrop,
  WordPressResetTarget,
} from '../../modules/wordpressPublishing/types';
import type { ICategory } from '../../types/damTypes';
import {
  getWordPressUiStatusColor,
  getWordPressUiStatusDescription,
  getWordPressUiStatusFromItem,
  getWordPressUiStatusLabel,
} from '../../modules/wordpressPublishing/uiStatus';
import type { WordPressUiStatus } from '../../modules/wordpressPublishing/uiStatus';

const RECIPE_TARGET = { width: 796, height: 530 };
const RECIPE_ASPECT = RECIPE_TARGET.width / RECIPE_TARGET.height;
const PRODUCT_TARGET = { width: 1800, height: 1440 };
const WINE_TARGET = { width: 1200, height: 1400 };
const SELECTION_ASSET_KEY = 'wpPublishing:selectedAssetId';
const SELECTION_TYPE_KEY = 'wpPublishing:selectedType';
const DEFAULT_LEFT_PANEL_WIDTH_RATIO = 0.42;
const MIN_LEFT_PANEL_WIDTH = 320;
const MIN_RIGHT_PANEL_WIDTH = 420;
const RESIZE_HANDLE_WIDTH = 14;
const ORIGINAL_PREVIEW_MAX_HEIGHT = 550;
const DEFAULT_PUBLICATION_LIST_LIMIT = 100;
const PUBLICATION_LIST_LIMIT_OPTIONS = [100, 200, 500];
const LIST_STATUS_CHIP_SX = {
  height: 20,
  borderRadius: '10px',
  '& .MuiChip-label': {
    px: 0.75,
    fontSize: '0.68rem',
    fontWeight: 500,
    whiteSpace: 'nowrap',
  },
} as const;

function getPendingChangeAlert(
  publication: WordPressPublicationItem['publication'],
  options: { usesQuoteBackground?: boolean; usesWineryImage?: boolean } = {},
) {
  if (!publication?.hasPendingChanges) return null;
  const pendingImageChange = !!publication.pendingImageChange;
  const pendingContentChange = !!publication.pendingContentChange;
  const pendingQuoteBackgroundChange = options.usesQuoteBackground !== false
    && !!publication.pendingQuoteBackgroundChange;
  const pendingWineryImageChange = options.usesWineryImage !== false
    && !!publication.pendingWineryImageChange;
  if (!pendingImageChange && !pendingContentChange && !pendingQuoteBackgroundChange && !pendingWineryImageChange) {
    return null;
  }

  if (pendingImageChange && pendingWineryImageChange) {
    return {
      severity: 'warning' as const,
      text: 'Bilden och winery image har ändrats sedan senaste WordPress-sync. Skapa en ny WP-bild vid behov och publicera sedan om.',
    };
  }
  if (pendingContentChange && pendingWineryImageChange) {
    return {
      severity: 'info' as const,
      text: 'Text eller winery image har ändrats sedan senaste WordPress-sync. Publicera om ändringarna till WordPress.',
    };
  }
  if (pendingImageChange && pendingContentChange && pendingQuoteBackgroundChange) {
    return {
      severity: 'warning' as const,
      text: 'Bilden, texten och quote bakgrundsbilden har ändrats sedan senaste WordPress-sync. Skapa en ny WP-bild och publicera sedan om.',
    };
  }
  if (pendingImageChange && pendingQuoteBackgroundChange) {
    return {
      severity: 'warning' as const,
      text: 'Bilden och quote bakgrundsbilden har ändrats sedan senaste WordPress-sync. Skapa en ny WP-bild och publicera sedan om.',
    };
  }
  if (pendingContentChange && pendingQuoteBackgroundChange) {
    return {
      severity: 'info' as const,
      text: 'Text eller quote bakgrundsbild har ändrats sedan senaste WordPress-sync. Publicera om ändringarna till WordPress.',
    };
  }
  if (pendingImageChange && pendingContentChange) {
    return {
      severity: 'warning' as const,
      text: 'Bilden och texten har ändrats sedan senaste WordPress-sync. Skapa en ny WP-bild och publicera sedan om.',
    };
  }
  if (pendingImageChange) {
    return {
      severity: 'warning' as const,
      text: 'En ny bildversion finns i DAM. Skapa en ny WP-bild innan du publicerar om.',
    };
  }
  if (pendingQuoteBackgroundChange) {
    return {
      severity: 'info' as const,
      text: 'Quote bakgrundsbilden har ändrats sedan senaste WordPress-sync. Publicera om ändringarna till WordPress.',
    };
  }
  if (pendingWineryImageChange) {
    return {
      severity: 'info' as const,
      text: 'Winery image har ändrats sedan senaste WordPress-sync. Publicera om ändringarna till WordPress.',
    };
  }
  if (pendingContentChange) {
    return {
      severity: 'info' as const,
      text: 'Text eller templatefält har ändrats sedan senaste WordPress-sync. Publicera om ändringarna till WordPress.',
    };
  }
  return null;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function toAbsoluteUrl(relativeUrl?: string | null) {
  if (!relativeUrl) return '';
  if (/^https?:\/\//i.test(relativeUrl)) return relativeUrl;
  return `${API_BASE_URL}${relativeUrl}`;
}

function MatchingAssetThumbnailHoverPreview(props: {
  previewUrl?: string | null;
  alt: string;
  sx?: React.ComponentProps<typeof Box>['sx'];
  children: React.ReactNode;
}) {
  const { previewUrl, alt, sx, children } = props;
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const absoluteUrl = previewUrl ? toAbsoluteUrl(previewUrl) : '';

  return (
    <>
      <Box
        ref={anchorRef}
        onMouseEnter={() => {
          if (absoluteUrl) setOpen(true);
        }}
        onMouseLeave={() => setOpen(false)}
        sx={sx}
      >
        {children}
      </Box>
      {absoluteUrl ? (
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement="right-start"
          modifiers={[{ name: 'offset', options: { offset: [10, 0] } }]}
          sx={{ zIndex: theme => theme.zIndex.modal }}
        >
          <Paper
            elevation={12}
            sx={{
              p: 1,
              maxWidth: 440,
              maxHeight: 440,
              bgcolor: 'background.paper',
              pointerEvents: 'none',
            }}
          >
            <Box
              component="img"
              src={absoluteUrl}
              alt={alt}
              sx={{ maxWidth: 400, maxHeight: 400, width: 'auto', height: 'auto', objectFit: 'contain', display: 'block' }}
            />
          </Paper>
        </Popper>
      ) : null}
    </>
  );
}

function toWordPressRenditionUrl(relativeUrl?: string | null, cacheBustKey?: string | null) {
  const absoluteUrl = toAbsoluteUrl(relativeUrl);
  if (!absoluteUrl) return '';
  if (!cacheBustKey) return absoluteUrl;

  const separator = absoluteUrl.includes('?') ? '&' : '?';
  return `${absoluteUrl}${separator}v=${encodeURIComponent(cacheBustKey)}`;
}

function normalizeRecipeRotationDeg(value: unknown): number {
  const n = Math.round(Number(value) || 0);
  const m = ((n % 360) + 360) % 360;
  if (m === 0 || m === 90 || m === 180 || m === 270) {
    return m;
  }
  return 0;
}

function recipeEffectiveReferencePixels(naturalWidth: number, naturalHeight: number, rotationDeg: number) {
  const w = Math.max(1, Math.round(naturalWidth || 1));
  const h = Math.max(1, Math.round(naturalHeight || 1));
  const r = normalizeRecipeRotationDeg(rotationDeg);
  if (r === 90 || r === 270) {
    return { width: h, height: w };
  }
  return { width: w, height: h };
}

function getDefaultRecipeCrop(referenceWidth: number, referenceHeight: number): WordPressRecipeCrop {
  const safeWidth = Math.max(1, Math.round(referenceWidth || 1));
  const safeHeight = Math.max(1, Math.round(referenceHeight || 1));
  const sourceAspect = safeWidth / safeHeight;

  let width = safeWidth;
  let height = safeHeight;
  if (sourceAspect > RECIPE_ASPECT) {
    width = Math.round(safeHeight * RECIPE_ASPECT);
    height = safeHeight;
  } else {
    width = safeWidth;
    height = Math.round(safeWidth / RECIPE_ASPECT);
  }

  return {
    x: Math.max(0, Math.round((safeWidth - width) / 2)),
    y: Math.max(0, Math.round((safeHeight - height) / 2)),
    width,
    height,
    zoom: 1,
    referenceWidth: safeWidth,
    referenceHeight: safeHeight,
    rotation: 0,
    flipHorizontal: false,
  };
}

function editorStateFromCrop(
  recipeCrop: WordPressRecipeCrop | null | undefined,
  naturalWidth: number,
  naturalHeight: number,
  effWidth: number,
  effHeight: number,
) {
  const fallback = getDefaultRecipeCrop(effWidth, effHeight);
  const crop = recipeCrop || fallback;
  const refWidth = Math.max(1, crop.referenceWidth || effWidth || 1);
  const refHeight = Math.max(1, crop.referenceHeight || effHeight || 1);
  const baseScale = Math.max(RECIPE_TARGET.width / refWidth, RECIPE_TARGET.height / refHeight);
  const zoomEff = RECIPE_TARGET.width / Math.max(1, baseScale * crop.width);
  const zoom = clamp(zoomEff, 1, 5);

  return {
    focusX: clamp(((crop.x + crop.width / 2) / refWidth) * 100, 0, 100),
    focusY: clamp(((crop.y + crop.height / 2) / refHeight) * 100, 0, 100),
    zoom,
  };
}

function buildRecipeCropFromEditor(
  naturalWidth: number,
  naturalHeight: number,
  effWidth: number,
  effHeight: number,
  focusX: number,
  focusY: number,
  zoom: number,
  rotationDeg: number,
  flipHorizontal: boolean,
): WordPressRecipeCrop {
  const refWidth = Math.max(1, Math.round(effWidth || 1));
  const refHeight = Math.max(1, Math.round(effHeight || 1));
  const zoomPan = zoom;
  const baseScale = Math.max(RECIPE_TARGET.width / refWidth, RECIPE_TARGET.height / refHeight);
  const scaledWidth = refWidth * baseScale * zoomPan;
  const scaledHeight = refHeight * baseScale * zoomPan;
  const centerX = (focusX / 100) * scaledWidth;
  const centerY = (focusY / 100) * scaledHeight;
  const left = clamp(centerX - RECIPE_TARGET.width / 2, 0, Math.max(0, scaledWidth - RECIPE_TARGET.width));
  const top = clamp(centerY - RECIPE_TARGET.height / 2, 0, Math.max(0, scaledHeight - RECIPE_TARGET.height));

  return {
    x: Math.round(left / (baseScale * zoomPan)),
    y: Math.round(top / (baseScale * zoomPan)),
    width: Math.max(1, Math.round(RECIPE_TARGET.width / (baseScale * zoomPan))),
    height: Math.max(1, Math.round(RECIPE_TARGET.height / (baseScale * zoomPan))),
    zoom,
    referenceWidth: refWidth,
    referenceHeight: refHeight,
    rotation: normalizeRecipeRotationDeg(rotationDeg),
    flipHorizontal,
  };
}

function selectionTabFromStorage(): WordPressPublishingTab {
  const stored = window.sessionStorage.getItem(SELECTION_TYPE_KEY);
  if (stored === 'product') {
    return 'dvh';
  }
  if (stored === 'dvh' || stored === 'foodservice' || stored === 'recipe' || stored === 'wine' || stored === 'matching' || stored === 'quote-backgrounds' || stored === 'winery-images' || stored === 'sync-warnings') {
    return stored;
  }
  return 'dvh';
}

function publicationTypeLabel(type: WordPressPublicationType) {
  switch (type) {
    case 'dvh':
      return 'DVH';
    case 'foodservice':
      return 'Food Service';
    case 'recipe':
      return 'Recept';
    case 'wine':
      return 'Vin';
    default:
      return type;
  }
}

function getRenditionAspectRatio(type: WordPressPublicationType) {
  switch (type) {
    case 'dvh':
    case 'foodservice':
      return `${PRODUCT_TARGET.width} / ${PRODUCT_TARGET.height}`;
    case 'wine':
      return `${WINE_TARGET.width} / ${WINE_TARGET.height}`;
    case 'recipe':
    default:
      return `${RECIPE_TARGET.width} / ${RECIPE_TARGET.height}`;
  }
}

function getResolutionLabel(resolution: WordPressCategoryRootResolution | undefined) {
  if (!resolution) return 'Inte vald';
  if (resolution.resolvedRootName && resolution.source === 'configured') {
    return `${resolution.resolvedRootName}`;
  }
  if (resolution.resolvedRootName && resolution.source === 'fallback') {
    return `${resolution.resolvedRootName} (fallback)`;
  }
  return 'Inte vald';
}

function formatAssetResolution(asset: { width?: number | null; height?: number | null } | null | undefined) {
  if (!asset?.width || !asset?.height) {
    return 'Okänd upplösning';
  }
  return `${asset.width}x${asset.height} px`;
}

function getWpVariantSourceLabel(source?: 'generated' | 'legacy_linked' | null) {
  switch (source) {
    case 'legacy_linked':
      return 'WP-variant från legacy-bild';
    case 'generated':
      return 'WP-variant från originalbild';
    default:
      return 'Ingen sparad WP-variant ännu';
  }
}

function getWpVariantSourceColor(source?: 'generated' | 'legacy_linked' | null): 'default' | 'success' | 'warning' {
  switch (source) {
    case 'legacy_linked':
      return 'warning';
    case 'generated':
      return 'success';
    default:
      return 'default';
  }
}

function getRecipePreviewPlacement(
  referenceWidth: number,
  referenceHeight: number,
  focusX: number,
  focusY: number,
  zoom: number,
) {
  const safeReferenceWidth = Math.max(1, referenceWidth || 1);
  const safeReferenceHeight = Math.max(1, referenceHeight || 1);
  const baseScale = Math.max(RECIPE_TARGET.width / safeReferenceWidth, RECIPE_TARGET.height / safeReferenceHeight);
  const renderedWidth = (safeReferenceWidth * baseScale * zoom / RECIPE_TARGET.width) * 100;
  const renderedHeight = (safeReferenceHeight * baseScale * zoom / RECIPE_TARGET.height) * 100;
  const left = -clamp((focusX / 100) * renderedWidth - 50, 0, Math.max(0, renderedWidth - 100));
  const top = -clamp((focusY / 100) * renderedHeight - 50, 0, Math.max(0, renderedHeight - 100));

  return {
    width: `${renderedWidth}%`,
    height: `${renderedHeight}%`,
    left: `${left}%`,
    top: `${top}%`,
  };
}

function WordPressPublishingPage() {
  const theme = useTheme();
  const isWideLayout = useMediaQuery(theme.breakpoints.up('xl'));
  const layoutRef = useRef<HTMLDivElement | null>(null);
  const [activeTab, setActiveTab] = useState<WordPressPublishingTab>(selectionTabFromStorage);
  const [summary, setSummary] = useState<WordPressPublishingSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<WordPressUiStatus | ''>('');
  const [itemsLimit, setItemsLimit] = useState(DEFAULT_PUBLICATION_LIST_LIMIT);
  const [onlyUnlinked, setOnlyUnlinked] = useState(true);
  const [items, setItems] = useState<WordPressPublicationItem[]>([]);
  const [itemsTotal, setItemsTotal] = useState(0);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [matchingItems, setMatchingItems] = useState<LegacyRecipeListItem[]>([]);
  const [matchingTotal, setMatchingTotal] = useState(0);
  const [matchingLoading, setMatchingLoading] = useState(false);
  const [matchingDetailTab, setMatchingDetailTab] = useState<'suggestions' | 'search'>('suggestions');
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [selectedLegacyAssetId, setSelectedLegacyAssetId] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<LegacyRecipeMatchSuggestion[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [manualAssetSearch, setManualAssetSearch] = useState('');
  const [manualAssetResults, setManualAssetResults] = useState<LegacyRecipeMatchSuggestion['asset'][]>([]);
  const [manualAssetSearchLoading, setManualAssetSearchLoading] = useState(false);
  const [manualAssetSearchPerformed, setManualAssetSearchPerformed] = useState(false);
  const [preserveOriginalOnMatch, setPreserveOriginalOnMatch] = useState(false);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [message, setMessage] = useState<{ severity: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [focusX, setFocusX] = useState(50);
  const [focusY, setFocusY] = useState(50);
  const [zoom, setZoom] = useState(1);
  const [recipeNaturalSize, setRecipeNaturalSize] = useState({ width: 1, height: 1 });
  const [recipeRotation, setRecipeRotation] = useState(0);
  const [recipeFlipHorizontal, setRecipeFlipHorizontal] = useState(false);
  const [recipeEditorPreviewUrl, setRecipeEditorPreviewUrl] = useState('');
  const [selectedBulkAssetIds, setSelectedBulkAssetIds] = useState<string[]>([]);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [bulkResetDialogOpen, setBulkResetDialogOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState<WordPressResetTarget>('all');
  const [resetConfirmation, setResetConfirmation] = useState('');
  const [rootCategories, setRootCategories] = useState<ICategory[]>([]);
  const [wordpressSettings, setWordpressSettings] = useState<WordPressPublishingSettingsDetail | null>(null);
  const [leftPanelWidth, setLeftPanelWidth] = useState<number | null>(null);
  const [isResizingPanels, setIsResizingPanels] = useState(false);
  const [renditionPreviewOpen, setRenditionPreviewOpen] = useState(false);
  const [fieldCheckFilter, setFieldCheckFilter] = useState<'' | 'missing'>('');
  const [syncWarningsFilter, setSyncWarningsFilter] = useState(false);
  const [syncWarningsReloadKey, setSyncWarningsReloadKey] = useState(0);
  const [fieldCheckDialogOpen, setFieldCheckDialogOpen] = useState(false);
  const [fieldCheckRechecking, setFieldCheckRechecking] = useState(false);
  const [publishConfirm, setPublishConfirm] = useState<{ item: WordPressPublicationItem; mode: 'publish' | 'request-sync' } | null>(null);
  const [bulkPublishSummary, setBulkPublishSummary] = useState<WordPressFieldCheckBulkSummary | null>(null);
  const [categoryRootsDraft, setCategoryRootsDraft] = useState<Record<WordPressPublicationType, string>>({
    dvh: '',
    foodservice: '',
    recipe: '',
    wine: '',
  });

  const selectedItem = useMemo(
    () => items.find(item => item.asset._id === selectedAssetId) || null,
    [items, selectedAssetId],
  );
  const selectedLegacyItem = useMemo(
    () => matchingItems.find(item => item.legacyAsset._id === selectedLegacyAssetId) || null,
    [matchingItems, selectedLegacyAssetId],
  );
  const selectedItemUiStatus = useMemo(
    () => getWordPressUiStatusFromItem(selectedItem),
    [selectedItem],
  );
  const selectedPublication = selectedItem?.publication || null;
  const selectedQuoteBackground = selectedItem?.quoteBackground || null;
  const selectedWineryImage = selectedItem?.wineryImage || null;
  const selectedItemUsesQuoteBackground = selectedItem?.contentType === 'product';
  const selectedItemUsesWineryImage = selectedItem?.contentType === 'wine';
  const selectedPendingAlert = useMemo(
    () => getPendingChangeAlert(selectedPublication, {
      usesQuoteBackground: selectedItemUsesQuoteBackground,
      usesWineryImage: selectedItemUsesWineryImage,
    }),
    [selectedItemUsesQuoteBackground, selectedItemUsesWineryImage, selectedPublication],
  );
  const selectedItemHasPendingImageChange = !!selectedPublication?.pendingImageChange;
  const selectedItemHasPendingContentChange = !!selectedPublication?.pendingContentChange;
  const selectedItemHasPendingQuoteBackgroundChange = selectedItemUsesQuoteBackground
    && !!selectedPublication?.pendingQuoteBackgroundChange;
  const selectedItemHasPendingWineryImageChange = selectedItemUsesWineryImage
    && !!selectedPublication?.pendingWineryImageChange;
  const selectedRecipePreservePending = selectedPublication?.recipeMatchMode === 'preserve_original'
    && selectedPublication?.preserveOriginalStatus === 'pending_crop';
  const selectedRecipePreserveCompleted = selectedPublication?.recipeMatchMode === 'preserve_original'
    && selectedPublication?.preserveOriginalStatus === 'completed';
  const selectedFieldCheckBlocked = isWordPressFieldCheckBlocked(selectedItem?.fieldCheck);
  const selectedPublicationIsPublished = selectedPublication?.wordpressStatus === 'published'
    || selectedPublication?.syncStatus === 'synced';
  const selectedRecipeCropSourceAsset = selectedRecipePreservePending
    ? (selectedItem?.cropSourceAsset || selectedItem?.asset || null)
    : (selectedItem?.asset || null);
  const selectedPublicationIsUnlockedDraft = selectedPublication?.wordpressStatus === 'draft'
    && (!selectedPublication.wordpressAction || selectedPublication.wordpressAction === 'none');
  const selectedPublicationHasPendingWordPressAction = selectedPublication?.syncStatus === 'sync_pending'
    && !selectedPublicationIsUnlockedDraft;
  const canGenerateSelectedItem = !!selectedPublication?.enabled && !selectedPublicationHasPendingWordPressAction;
  const canPublishSelectedItem = !!selectedPublication?.enabled
    && !!selectedPublication?.rendition
    && !selectedPublicationHasPendingWordPressAction
    && (
      selectedPublication.approvalStatus !== 'approved'
      || selectedPublication.syncStatus === 'sync_error'
      || selectedItemUiStatus === 'unpublished'
    );
  const canRequestSyncSelectedItem = !!selectedPublication?.enabled
    && !!selectedPublication?.rendition
    && selectedPublication.approvalStatus === 'approved'
    && !selectedPublicationHasPendingWordPressAction
    && (selectedItemHasPendingContentChange || selectedItemHasPendingQuoteBackgroundChange)
    && !selectedItemHasPendingImageChange;
  const canUnpublishSelectedItem = !!selectedPublication
    && !selectedPublicationHasPendingWordPressAction
    && selectedPublication.wordpressStatus !== 'draft'
    && (
      !!selectedPublication.wordpressRemoteId
      || selectedPublication.wordpressStatus === 'published'
      || selectedPublication.syncStatus === 'synced'
    );
  const visibleItemIds = useMemo(() => items.map(item => item.asset._id), [items]);
  const allVisibleSelected = visibleItemIds.length > 0 && visibleItemIds.every(assetId => selectedBulkAssetIds.includes(assetId));
  const someVisibleSelected = visibleItemIds.some(assetId => selectedBulkAssetIds.includes(assetId));
  const isMatchingTab = activeTab === 'matching';
  const isQuoteBackgroundTab = activeTab === 'quote-backgrounds';
  const isWineryImagesTab = activeTab === 'winery-images';
  const isSyncWarningsTab = activeTab === 'sync-warnings';
  const isPublicationTab = !isMatchingTab && !isQuoteBackgroundTab && !isWineryImagesTab && !isSyncWarningsTab;
  const publicationTab = isPublicationTab ? activeTab as WordPressPublicationType : null;
  const activeTabResolution = publicationTab ? wordpressSettings?.resolutions?.[publicationTab] : undefined;

  const storeSelection = useCallback((type: WordPressPublishingTab, assetId?: string | null) => {
    window.sessionStorage.setItem(SELECTION_TYPE_KEY, type);
    if (assetId) {
      window.sessionStorage.setItem(SELECTION_ASSET_KEY, assetId);
    } else {
      window.sessionStorage.removeItem(SELECTION_ASSET_KEY);
    }
  }, []);

  const refreshSummary = useCallback(async () => {
    try {
      setSummaryLoading(true);
      const nextSummary = await fetchWordPressPublishingSummary();
      setSummary(nextSummary);
      window.dispatchEvent(new CustomEvent('wordpress-publishing:summary-updated', {
        detail: {
          pendingAssetCount: nextSummary.pendingAssetCount,
          pendingAssetCountByType: nextSummary.pendingAssetCountByType,
        },
      }));
    } catch (error: any) {
      setMessage({ severity: 'error', text: error?.response?.data?.message || error?.message || 'Kunde inte hämta WordPress-översikten.' });
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const refreshSettings = useCallback(async () => {
    try {
      setSettingsLoading(true);
      const [nextSettings, nextRootCategories] = await Promise.all([
        fetchWordPressPublishingSettings(),
        fetchWordPressRootCategories(),
      ]);
      setWordpressSettings(nextSettings);
      setRootCategories(nextRootCategories);
      setCategoryRootsDraft({
        dvh: nextSettings.settings.categoryRoots.dvh || '',
        foodservice: nextSettings.settings.categoryRoots.foodservice || '',
        recipe: nextSettings.settings.categoryRoots.recipe || '',
        wine: nextSettings.settings.categoryRoots.wine || '',
      });
    } catch (error: any) {
      setMessage({ severity: 'error', text: error?.response?.data?.message || error?.message || 'Kunde inte läsa WordPress-inställningarna.' });
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  const applyAutoSelection = useCallback((loadedItems: WordPressPublicationItem[]) => {
    const storedType = window.sessionStorage.getItem(SELECTION_TYPE_KEY);
    const storedAssetId = window.sessionStorage.getItem(SELECTION_ASSET_KEY);
    if (storedType === activeTab && storedAssetId && loadedItems.some(item => item.asset._id === storedAssetId)) {
      setSelectedAssetId(storedAssetId);
      return;
    }

    if (selectedAssetId && loadedItems.some(item => item.asset._id === selectedAssetId)) {
      return;
    }

    setSelectedAssetId(loadedItems[0]?.asset._id || null);
  }, [activeTab, selectedAssetId]);

  const loadPublicationItems = useCallback(async () => {
    if (!publicationTab) return;
    try {
      setItemsLoading(true);
      const response = await fetchWordPressPublicationItems({
        type: publicationTab,
        search,
        status: statusFilter || undefined,
        fieldCheck: fieldCheckFilter || undefined,
        syncWarnings: syncWarningsFilter ? 'only' : undefined,
        limit: itemsLimit,
      });
      setItems(response.items);
      setItemsTotal(response.total);
      applyAutoSelection(response.items);
    } catch (error: any) {
      setMessage({ severity: 'error', text: error?.response?.data?.message || error?.message || 'Kunde inte hämta WordPress-poster.' });
    } finally {
      setItemsLoading(false);
    }
  }, [publicationTab, search, statusFilter, fieldCheckFilter, syncWarningsFilter, itemsLimit, applyAutoSelection]);

  const loadMatchingItems = useCallback(async () => {
    if (!isMatchingTab) return;
    try {
      setMatchingLoading(true);
      const response = await fetchLegacyRecipeItems(search, onlyUnlinked);
      setMatchingItems(response.items);
      setMatchingTotal(response.total);
      const storedAssetId = window.sessionStorage.getItem(SELECTION_ASSET_KEY);
      if (window.sessionStorage.getItem(SELECTION_TYPE_KEY) === 'matching' && storedAssetId && response.items.some(item => item.legacyAsset._id === storedAssetId)) {
        setSelectedLegacyAssetId(storedAssetId);
      } else if (selectedLegacyAssetId && response.items.some(item => item.legacyAsset._id === selectedLegacyAssetId)) {
        // keep current selection
      } else {
        setSelectedLegacyAssetId(response.items[0]?.legacyAsset._id || null);
      }
    } catch (error: any) {
      setMessage({ severity: 'error', text: error?.response?.data?.message || error?.message || 'Kunde inte hämta matchningslistan.' });
    } finally {
      setMatchingLoading(false);
    }
  }, [isMatchingTab, search, onlyUnlinked, selectedLegacyAssetId]);

  useEffect(() => {
    refreshSummary();
  }, [refreshSummary]);

  useEffect(() => {
    refreshSettings();
  }, [refreshSettings]);

  useEffect(() => {
    if (isMatchingTab) {
      loadMatchingItems();
    } else if (isPublicationTab) {
      loadPublicationItems();
    }
  }, [isMatchingTab, isPublicationTab, loadPublicationItems, loadMatchingItems]);

  useEffect(() => {
    if (!selectedItem || activeTab !== 'recipe') return;
    const assetW = Math.max(1, selectedRecipeCropSourceAsset?.width || 1);
    const assetH = Math.max(1, selectedRecipeCropSourceAsset?.height || 1);
    setRecipeNaturalSize({ width: assetW, height: assetH });

    const crop = selectedItem.publication?.recipeCrop;
    const rot = normalizeRecipeRotationDeg(crop?.rotation);
    setRecipeRotation(rot);
    setRecipeFlipHorizontal(Boolean(crop?.flipHorizontal));

    const effective = recipeEffectiveReferencePixels(assetW, assetH, rot);
    const nextEditor = editorStateFromCrop(crop || null, assetW, assetH, effective.width, effective.height);
    setFocusX(nextEditor.focusX);
    setFocusY(nextEditor.focusY);
    setZoom(nextEditor.zoom);
  }, [selectedItem, activeTab, selectedRecipeCropSourceAsset]);

  useEffect(() => {
    if (!isMatchingTab || !selectedLegacyAssetId) {
      setSuggestions([]);
      setManualAssetSearch('');
      setManualAssetResults([]);
      setManualAssetSearchPerformed(false);
      setMatchingDetailTab('suggestions');
      return;
    }
    setSuggestions([]);
    setManualAssetSearch('');
    setManualAssetResults([]);
    setManualAssetSearchPerformed(false);
    setMatchingDetailTab('suggestions');
  }, [isMatchingTab, selectedLegacyAssetId]);

  useEffect(() => {
    if (isMatchingTab || isQuoteBackgroundTab || isWineryImagesTab || isSyncWarningsTab) {
      setSelectedBulkAssetIds([]);
      return;
    }

    setSelectedBulkAssetIds(previousSelection =>
      previousSelection.filter(assetId => visibleItemIds.includes(assetId)),
    );
  }, [isMatchingTab, isQuoteBackgroundTab, isWineryImagesTab, isSyncWarningsTab, visibleItemIds]);

  const runAction = useCallback(async (actionKey: string, runner: () => Promise<void>) => {
    try {
      setBusyAction(actionKey);
      await runner();
    } catch (error: any) {
      setMessage({ severity: 'error', text: error?.response?.data?.message || error?.message || 'Åtgärden misslyckades.' });
    } finally {
      setBusyAction(null);
    }
  }, []);

  const refreshCurrentTab = useCallback(async () => {
    await refreshSummary();
    if (isMatchingTab) {
      await loadMatchingItems();
    } else if (publicationTab) {
      await loadPublicationItems();
    } else if (isSyncWarningsTab) {
      setSyncWarningsReloadKey(key => key + 1);
    }
  }, [isMatchingTab, isSyncWarningsTab, publicationTab, loadMatchingItems, loadPublicationItems, refreshSummary]);

  const toggleBulkSelection = useCallback((assetId: string, checked: boolean) => {
    setSelectedBulkAssetIds(previousSelection => {
      if (checked) {
        return previousSelection.includes(assetId) ? previousSelection : [...previousSelection, assetId];
      }
      return previousSelection.filter(existingId => existingId !== assetId);
    });
  }, []);

  const toggleSelectAllVisible = useCallback((checked: boolean) => {
    if (checked) {
      setSelectedBulkAssetIds(previousSelection => Array.from(new Set([...previousSelection, ...visibleItemIds])));
      return;
    }
    setSelectedBulkAssetIds(previousSelection => previousSelection.filter(assetId => !visibleItemIds.includes(assetId)));
  }, [visibleItemIds]);

  const handleSaveSettings = async () => {
    try {
      setSettingsSaving(true);
      const nextSettings = await saveWordPressPublishingSettings({
        dvh: categoryRootsDraft.dvh || null,
        foodservice: categoryRootsDraft.foodservice || null,
        recipe: categoryRootsDraft.recipe || null,
        wine: categoryRootsDraft.wine || null,
      });
      setWordpressSettings(nextSettings);
      setSettingsDialogOpen(false);
      setMessage({ severity: 'success', text: 'WordPress-inställningarna är sparade.' });
      await refreshCurrentTab();
    } catch (error: any) {
      setMessage({ severity: 'error', text: error?.response?.data?.message || error?.message || 'Kunde inte spara WordPress-inställningarna.' });
    } finally {
      setSettingsSaving(false);
    }
  };

  const summarizeBulkResult = (result: WordPressBulkActionResult) => {
    if (result.failedCount === 0) {
      return `${result.successCount} av ${result.totalAssets} poster uppdaterades.`;
    }

    const firstFailure = result.failures[0];
    return `${result.successCount} lyckades, ${result.failedCount} misslyckades.${firstFailure ? ` Första fel: ${firstFailure.assetId} - ${firstFailure.message}` : ''}`;
  };

  const runBulkAction = async (
    action: 'enable' | 'generate' | 'publish' | 'unpublish' | 'reset',
    assetIds: string[],
    skippedForRequiredFields = 0,
    syncWarningItemCount = 0,
  ) => {
    if (!publicationTab || assetIds.length === 0) return;

    await runAction(`bulk-${action}`, async () => {
      const result = await runWordPressBulkAction({
        assetIds,
        type: publicationTab,
        action: action === 'publish' ? 'approve' : action,
      });

      const skippedText = skippedForRequiredFields > 0
        ? ` ${skippedForRequiredFields} hoppades över eftersom obligatoriska fält saknas.`
        : '';
      const syncWarningText = syncWarningItemCount > 0
        ? ` ${syncWarningItemCount} av posterna har synkvarningar från senaste synken.`
        : '';
      setMessage({
        severity: result.failedCount > 0 ? 'error' : 'success',
        text: `${summarizeBulkResult(result)}${skippedText}${syncWarningText}`,
      });

      if (result.failedCount === 0) {
        setSelectedBulkAssetIds(previousSelection => previousSelection.filter(assetId => !assetIds.includes(assetId)));
      }

      await refreshCurrentTab();
    });
  };

  const handleBulkAction = async (action: 'enable' | 'generate' | 'publish' | 'unpublish' | 'reset') => {
    if (!publicationTab || selectedBulkAssetIds.length === 0) return;

    if (action === 'publish') {
      const selectedItems = items.filter(item => selectedBulkAssetIds.includes(item.asset._id));
      const fieldCheckSummary = summarizeWordPressFieldCheckForItems(selectedItems);
      if (fieldCheckSummary.warningIds.length > 0 || fieldCheckSummary.blockedIds.length > 0) {
        setBulkPublishSummary(fieldCheckSummary);
        return;
      }
      await runBulkAction(action, selectedBulkAssetIds, 0, countWordPressItemsWithSyncWarnings(selectedItems));
      return;
    }

    await runBulkAction(action, selectedBulkAssetIds);
  };

  const handleBulkPublishConfirmed = async (completeOnly: boolean) => {
    if (!bulkPublishSummary) return;
    const assetIds = completeOnly
      ? bulkPublishSummary.completeIds
      : [...bulkPublishSummary.completeIds, ...bulkPublishSummary.warningIds];
    const skippedCount = bulkPublishSummary.blockedIds.length;
    setBulkPublishSummary(null);
    await runBulkAction('publish', assetIds, skippedCount);
  };

  const bulkPublishSyncWarningItemCount = useMemo(() => {
    if (!bulkPublishSummary) return 0;
    const summaryIds = new Set([
      ...bulkPublishSummary.completeIds,
      ...bulkPublishSummary.warningIds,
      ...bulkPublishSummary.blockedIds,
    ]);
    return countWordPressItemsWithSyncWarnings(items.filter(item => summaryIds.has(item.asset._id)));
  }, [bulkPublishSummary, items]);

  const handleGlobalReset = async () => {
    if (!resetConfirmationMatches) return;

    await runAction('global-reset', async () => {
      const result = await resetWordPressPublications({
        target: resetTarget,
        confirmation: WORDPRESS_PUBLISHING_RESET_CONFIRMATION,
      });

      setResetConfirmation('');
      setMessage({
        severity: 'success',
        text: `Nollställde ${result.modifiedCount} av ${result.matchedCount} WordPress-publiceringar.`,
      });
      await refreshSummary();
      await refreshCurrentTab();
    });
  };

  const handleEnableChange = async (item: WordPressPublicationItem, enabled: boolean) => {
    storeSelection(activeTab, item.asset._id);
    await runAction(`enable-${item.asset._id}`, async () => {
      await setWordPressPublicationEnabled(item.asset._id, item.inferredType, enabled);
      setMessage({ severity: 'success', text: enabled ? 'Asseten ingår nu i WordPress-publicering.' : 'Asseten tas inte längre med i löpande WordPress-publicering.' });
      await refreshCurrentTab();
    });
  };

  const handleGenerate = async (item: WordPressPublicationItem) => {
    const effective = {
      width: Math.max(1, recipeNaturalSize.width || 1),
      height: Math.max(1, recipeNaturalSize.height || 1),
    };
    const recipeCrop = item.inferredType === 'recipe'
      ? buildRecipeCropFromEditor(
          recipeNaturalSize.width,
          recipeNaturalSize.height,
          effective.width,
          effective.height,
          focusX,
          focusY,
          zoom,
          recipeRotation,
          recipeFlipHorizontal,
        )
      : null;

    storeSelection(activeTab, item.asset._id);
    await runAction(`generate-${item.asset._id}`, async () => {
      await generateWordPressPublicationImage(item.asset._id, item.inferredType, recipeCrop);
      const preservePending = item.publication?.recipeMatchMode === 'preserve_original'
        && item.publication?.preserveOriginalStatus === 'pending_crop';
      setMessage({
        severity: 'success',
        text: preservePending
          ? 'WP-bilden och den nya högupplösta receptbilden har skapats. Originalet är bevarat.'
          : 'WP-bilden har skapats.',
      });
      await refreshCurrentTab();
    });
  };

  const refreshPublicationItem = useCallback(async (item: WordPressPublicationItem) => {
    const freshItem = await fetchWordPressPublicationItem(item.asset._id, item.inferredType);
    if (freshItem) {
      setItems(previousItems => previousItems.map(entry => (entry.asset._id === freshItem.asset._id ? freshItem : entry)));
    }
    return freshItem;
  }, []);

  const executePublishAction = async (item: WordPressPublicationItem, mode: 'publish' | 'request-sync') => {
    storeSelection(activeTab, item.asset._id);
    await runAction(`${mode}-${item.asset._id}`, async () => {
      if (mode === 'publish') {
        await approveWordPressPublication(item.asset._id, item.inferredType, true);
        setMessage({ severity: 'success', text: 'Posten är skickad för publicering till WordPress.' });
      } else {
        await requestWordPressPublicationSync(item.asset._id, item.inferredType);
        setMessage({ severity: 'success', text: 'Ändringarna är skickade för ompublicering till WordPress.' });
      }
      await refreshCurrentTab();
    });
  };

  const startPublishFlow = async (item: WordPressPublicationItem, mode: 'publish' | 'request-sync') => {
    storeSelection(activeTab, item.asset._id);
    let currentItem = item;
    try {
      setBusyAction(`field-check-${item.asset._id}`);
      currentItem = (await refreshPublicationItem(item)) || item;
    } catch (error: any) {
      setMessage({ severity: 'error', text: error?.response?.data?.message || error?.message || 'Kunde inte kontrollera fälten.' });
      return;
    } finally {
      setBusyAction(null);
    }

    if (isWordPressFieldCheckBlocked(currentItem.fieldCheck)) {
      const requiredMissing = getWordPressFieldCheckRequiredMissing(currentItem.fieldCheck);
      setMessage({
        severity: 'error',
        text: `Posten kan inte publiceras. Obligatoriska fält saknas: ${formatWordPressFieldCheckMissingLabels(requiredMissing)}.`,
      });
      return;
    }
    if (currentItem.fieldCheck?.status === 'warnings') {
      setPublishConfirm({ item: currentItem, mode });
      return;
    }

    await executePublishAction(currentItem, mode);
  };

  const handlePublish = (item: WordPressPublicationItem) => startPublishFlow(item, 'publish');

  const handleRequestSync = (item: WordPressPublicationItem) => startPublishFlow(item, 'request-sync');

  const handlePublishConfirmed = async () => {
    if (!publishConfirm) return;
    const { item, mode } = publishConfirm;
    setPublishConfirm(null);
    await executePublishAction(item, mode);
  };

  const handleRecheckSelectedItem = async () => {
    if (!selectedItem) return;
    try {
      setFieldCheckRechecking(true);
      await refreshPublicationItem(selectedItem);
    } catch (error: any) {
      setMessage({ severity: 'error', text: error?.response?.data?.message || error?.message || 'Kunde inte kontrollera fälten.' });
    } finally {
      setFieldCheckRechecking(false);
    }
  };

  const handleUnpublish = async (item: WordPressPublicationItem) => {
    storeSelection(activeTab, item.asset._id);
    await runAction(`unpublish-${item.asset._id}`, async () => {
      await requestWordPressPublicationUnpublish(item.asset._id, item.inferredType);
      setMessage({ severity: 'success', text: 'Posten är skickad för avpublicering från WordPress.' });
      await refreshCurrentTab();
    });
  };

  const handleLoadSuggestions = async (legacyAssetId: string) => {
    setMatchingDetailTab('suggestions');
    storeSelection('matching', legacyAssetId);
    await runAction(`suggestions-${legacyAssetId}`, async () => {
      setSuggestionsLoading(true);
      const nextSuggestions = await fetchLegacyRecipeSuggestions(legacyAssetId);
      setSuggestions(nextSuggestions);
      if (nextSuggestions.length === 0) {
        setMessage({ severity: 'info', text: 'Inga starka förslag hittades just nu. Prova att uppdatera matchindex.' });
      }
    });
    setSuggestionsLoading(false);
  };

  const handleManualAssetSearch = useCallback(async () => {
    if (!selectedLegacyItem) return;

    const trimmedSearch = manualAssetSearch.trim();
    if (trimmedSearch.length < 2) {
      setManualAssetResults([]);
      setManualAssetSearchPerformed(false);
      return;
    }

    try {
      setManualAssetSearchLoading(true);
      setManualAssetSearchPerformed(true);
      const nextResults = await searchLegacyRecipeSourceAssets(
        selectedLegacyItem.legacyAsset._id,
        trimmedSearch,
        MANUAL_DAM_SEARCH_LIMIT,
      );
      setManualAssetResults(nextResults);
    } catch (error: any) {
      setMessage({ severity: 'error', text: error?.response?.data?.message || error?.message || 'Kunde inte söka DAM-bilder för manuell koppling.' });
    } finally {
      setManualAssetSearchLoading(false);
    }
  }, [manualAssetSearch, selectedLegacyItem]);

  const handleLinkLegacyAsset = async (sourceAssetId: string, legacyAssetId: string) => {
    storeSelection('matching', legacyAssetId);
    await runAction(`link-${legacyAssetId}-${sourceAssetId}`, async () => {
      await linkLegacyRecipeToSource(sourceAssetId, legacyAssetId, preserveOriginalOnMatch);
      if (preserveOriginalOnMatch) {
        const linkedRecipeItem = await fetchWordPressPublicationItem(legacyAssetId, 'recipe');
        if (linkedRecipeItem) {
          setItems([linkedRecipeItem]);
        }
        setSearch('');
        setStatusFilter('');
        setActiveTab('recipe');
        setSelectedAssetId(legacyAssetId);
        storeSelection('recipe', legacyAssetId);
        setMessage({ severity: 'success', text: 'Kopplingen sparades. Originalbilden är bevarad - gå vidare med beskärning i Recept för att skapa WP-bild och ny högupplöst receptbild.' });
        await refreshSummary();
      } else {
        setMessage({ severity: 'success', text: 'Kopplingen sparades, receptbilden uppgraderades, originalet arkiverades och nuvarande WP-variant bevarades.' });
        await refreshCurrentTab();
      }
      setSuggestions([]);
      setManualAssetSearch('');
      setManualAssetResults([]);
      setManualAssetSearchPerformed(false);
    });
  };

  const handleReindexMatching = async () => {
    await runAction('matching-reindex', async () => {
      await reindexLegacyRecipeMatching();
      setMessage({ severity: 'success', text: 'Matchindexeringen startades i bakgrunden.' });
    });
  };

  const selectedPreviewUrl = selectedRecipeCropSourceAsset?.previewUrl ? toAbsoluteUrl(selectedRecipeCropSourceAsset.previewUrl) : '';
  const selectedRenditionUrl = selectedItem?.publication?.rendition?.url
    ? toWordPressRenditionUrl(
        selectedItem.publication.rendition.url,
        selectedItem.publication.rendition.generatedAt || selectedItem.publication.updatedAt || null,
      )
    : '';

  useEffect(() => {
    if (!selectedPreviewUrl || selectedItem?.inferredType !== 'recipe') {
      setRecipeEditorPreviewUrl('');
      return;
    }

    let cancelled = false;
    const rotation = normalizeRecipeRotationDeg(recipeRotation);
    const flipHorizontal = recipeFlipHorizontal;

    const image = new Image();
    image.onload = () => {
      if (cancelled) return;

      const sourceWidth = Math.max(1, image.naturalWidth || 1);
      const sourceHeight = Math.max(1, image.naturalHeight || 1);

      if (rotation === 0 && !flipHorizontal) {
        setRecipeNaturalSize({ width: sourceWidth, height: sourceHeight });
        setRecipeEditorPreviewUrl(selectedPreviewUrl);
        return;
      }

      const swapsAxes = rotation === 90 || rotation === 270;
      const canvas = document.createElement('canvas');
      canvas.width = swapsAxes ? sourceHeight : sourceWidth;
      canvas.height = swapsAxes ? sourceWidth : sourceHeight;

      const context = canvas.getContext('2d');
      if (!context) {
        setRecipeNaturalSize({ width: sourceWidth, height: sourceHeight });
        setRecipeEditorPreviewUrl(selectedPreviewUrl);
        return;
      }

      context.translate(canvas.width / 2, canvas.height / 2);
      if (flipHorizontal) {
        context.scale(-1, 1);
      }
      if (rotation !== 0) {
        context.rotate((rotation * Math.PI) / 180);
      }
      context.drawImage(image, -sourceWidth / 2, -sourceHeight / 2);

      try {
        setRecipeNaturalSize({ width: canvas.width, height: canvas.height });
        setRecipeEditorPreviewUrl(canvas.toDataURL('image/png'));
      } catch {
        setRecipeNaturalSize({ width: sourceWidth, height: sourceHeight });
        setRecipeEditorPreviewUrl(selectedPreviewUrl);
      }
    };
    image.onerror = () => {
      if (cancelled) return;
      setRecipeEditorPreviewUrl(selectedPreviewUrl);
    };
    image.src = selectedPreviewUrl;

    return () => {
      cancelled = true;
    };
  }, [selectedPreviewUrl, selectedItem?.inferredType, recipeRotation, recipeFlipHorizontal]);

  const selectedQuoteBackgroundUrl = selectedQuoteBackground?.rendition?.url
    ? toWordPressRenditionUrl(
        selectedQuoteBackground.rendition.url,
        selectedQuoteBackground.pendingUpdatedAt
          || selectedQuoteBackground.rendition.generatedAt
          || selectedQuoteBackground.mappingId
          || null,
      )
    : '';
  const selectedWineryImageUrl = selectedWineryImage?.rendition?.url
    ? toWordPressRenditionUrl(
        selectedWineryImage.rendition.url,
        selectedWineryImage.pendingUpdatedAt
          || selectedWineryImage.rendition.generatedAt
          || selectedWineryImage.mappingId
          || null,
      )
    : '';
  const recipeEffectiveRef = useMemo(
    () => ({
      width: Math.max(1, recipeNaturalSize.width || 1),
      height: Math.max(1, recipeNaturalSize.height || 1),
    }),
    [recipeNaturalSize.width, recipeNaturalSize.height],
  );

  const recipePreviewPlacement = useMemo(() => {
    return getRecipePreviewPlacement(
      recipeEffectiveRef.width,
      recipeEffectiveRef.height,
      focusX,
      focusY,
      zoom,
    );
  }, [
    recipeEffectiveRef.width,
    recipeEffectiveRef.height,
    focusX,
    focusY,
    zoom,
  ]);

  const handleRecipeRotate90 = useCallback(() => {
    setRecipeRotation(prev => (prev + 90) % 360);
    setFocusX(50);
    setFocusY(50);
    setZoom(1);
  }, []);

  const updateFocusFromPointer = (event: React.PointerEvent<HTMLDivElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const nextX = ((event.clientX - bounds.left) / bounds.width) * 100;
    const nextY = ((event.clientY - bounds.top) / bounds.height) * 100;
    setFocusX(clamp(nextX, 0, 100));
    setFocusY(clamp(nextY, 0, 100));
  };

  const openAssetDetailsPanel = useCallback((assetId: string) => {
    window.dispatchEvent(new CustomEvent('dam:open-asset-details', {
      detail: {
        assetId,
        preserveCurrentView: true,
      },
    }));
  }, []);

  const openSyncWarningAsset = useCallback((target: WordPressPublicationType, assetId: string) => {
    setSearch('');
    setStatusFilter('');
    setFieldCheckFilter('');
    setSyncWarningsFilter(true);
    setSelectedAssetId(assetId);
    storeSelection(target, assetId);
    setActiveTab(target);
  }, [storeSelection]);

  const statusOptions = useMemo(() => ([
    { value: '', label: 'Alla statusar' },
    { value: 'unselected', label: 'Ej vald' },
    { value: 'needs_image', label: 'Behöver WP-bild' },
    { value: 'ready_to_publish', label: 'Klar att publicera' },
    { value: 'changes_pending', label: 'Ändringar väntar' },
    { value: 'publishing', label: 'Publiceras' },
    { value: 'unpublishing', label: 'Avpubliceras' },
    { value: 'published', label: 'Publicerad' },
    { value: 'unpublished', label: 'Avpublicerad' },
    { value: 'error', label: 'Fel' },
  ]), []);
  const resetTargetOptions = useMemo<Array<{ value: WordPressResetTarget; label: string }>>(() => ([
    { value: 'all', label: 'Alla targets' },
    ...(['dvh', 'foodservice', 'wine', 'recipe'] as WordPressPublicationType[]).map(type => ({
      value: type,
      label: publicationTypeLabel(type),
    })),
  ]), []);
  const resetConfirmationMatches = resetConfirmation.trim() === WORDPRESS_PUBLISHING_RESET_CONFIRMATION;
  const hasActiveFilters = useMemo(() => (
    isMatchingTab
      ? Boolean(search) || !onlyUnlinked
      : isPublicationTab
        ? Boolean(search || statusFilter || fieldCheckFilter || syncWarningsFilter || itemsLimit !== DEFAULT_PUBLICATION_LIST_LIMIT)
        : false
  ), [isMatchingTab, isPublicationTab, onlyUnlinked, search, statusFilter, fieldCheckFilter, syncWarningsFilter, itemsLimit]);

  const resetFilters = useCallback(() => {
    setSearch('');
    setStatusFilter('');
    setFieldCheckFilter('');
    setSyncWarningsFilter(false);
    setItemsLimit(DEFAULT_PUBLICATION_LIST_LIMIT);
    setOnlyUnlinked(true);
  }, []);

  const openRenditionPreview = useCallback(() => {
    if (!selectedRenditionUrl) return;
    setRenditionPreviewOpen(true);
  }, [selectedRenditionUrl]);

  const closeRenditionPreview = useCallback(() => {
    setRenditionPreviewOpen(false);
  }, []);

  const handleRenditionPreviewKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openRenditionPreview();
    }
  }, [openRenditionPreview]);

  const adjustPaneWidth = useCallback((delta: number) => {
    const layout = layoutRef.current;
    if (!layout || !isWideLayout) return;

    const maxLeft = Math.max(MIN_LEFT_PANEL_WIDTH, layout.clientWidth - MIN_RIGHT_PANEL_WIDTH - RESIZE_HANDLE_WIDTH);
    const baseWidth = leftPanelWidth ?? Math.round(layout.clientWidth * DEFAULT_LEFT_PANEL_WIDTH_RATIO);
    const nextWidth = Math.min(maxLeft, Math.max(MIN_LEFT_PANEL_WIDTH, baseWidth + delta));
    setLeftPanelWidth(nextWidth);
  }, [isWideLayout, leftPanelWidth]);

  useEffect(() => {
    if (!selectedRenditionUrl) {
      setRenditionPreviewOpen(false);
    }
  }, [selectedRenditionUrl]);

  useEffect(() => {
    if (!isWideLayout) {
      setIsResizingPanels(false);
    }
  }, [isWideLayout]);

  useEffect(() => {
    if (!isWideLayout || !isResizingPanels) return;

    const handleMouseMove = (event: MouseEvent) => {
      const layout = layoutRef.current;
      if (!layout) return;

      const rect = layout.getBoundingClientRect();
      const maxLeft = Math.max(MIN_LEFT_PANEL_WIDTH, rect.width - MIN_RIGHT_PANEL_WIDTH - RESIZE_HANDLE_WIDTH);
      const nextWidth = Math.min(
        maxLeft,
        Math.max(MIN_LEFT_PANEL_WIDTH, event.clientX - rect.left - RESIZE_HANDLE_WIDTH / 2),
      );

      setLeftPanelWidth(nextWidth);
    };

    const stopResizing = () => setIsResizingPanels(false);

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', stopResizing);

    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', stopResizing);
    };
  }, [isWideLayout, isResizingPanels]);

  const summaryChips: Array<{ label: string; value: number | string; warn?: boolean }> = [
    { label: 'Totalt', value: summaryLoading ? '...' : summary?.total ?? 0 },
    { label: 'Valda för WordPress', value: summaryLoading ? '...' : summary?.activated ?? 0 },
    { label: 'Behöver bild', value: summaryLoading ? '...' : summary?.withoutImage ?? 0, warn: (summary?.withoutImage ?? 0) > 0 },
    { label: 'Klara att publicera', value: summaryLoading ? '...' : summary?.readyToSync ?? 0 },
    { label: 'Publicerade', value: summaryLoading ? '...' : summary?.synced ?? 0 },
    { label: 'Fel', value: summaryLoading ? '...' : summary?.syncErrors ?? 0, warn: (summary?.syncErrors ?? 0) > 0 },
  ];

  const renderPublicationTabLabel = useCallback((type: WordPressPublicationType, label: string) => {
    const total = summary?.byType[type] ?? 0;
    const pendingCount = Math.max(0, Number(summary?.pendingAssetCountByType?.[type] || 0));
    const text = `${label}${summary ? ` (${total})` : ''}`;

    if (pendingCount <= 0) {
      return text;
    }

    return (
      <Badge
        badgeContent={pendingCount}
        color="error"
        overlap="rectangular"
        sx={{
          '& .MuiBadge-badge': {
            fontSize: '0.6rem',
            minWidth: 16,
            height: 16,
            top: 3,
            right: -6,
          },
        }}
      >
        <Box component="span" sx={{ pr: 0.5 }}>
          {text}
        </Box>
      </Badge>
    );
  }, [summary]);

  const syncWarningGroupCount = Math.max(0, Number(summary?.syncWarningGroupCount || 0));
  const syncWarningsTabLabel = syncWarningGroupCount > 0 ? (
    <Badge
      badgeContent={syncWarningGroupCount}
      color="warning"
      max={999}
      overlap="rectangular"
      sx={{
        '& .MuiBadge-badge': {
          fontSize: '0.6rem',
          minWidth: 16,
          height: 16,
          top: 3,
          right: -6,
        },
      }}
    >
      <Box component="span" sx={{ pr: 0.5 }}>
        Saknas i WordPress
      </Box>
    </Badge>
  ) : 'Saknas i WordPress';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, height: '100%', overflow: 'hidden', p: 2, pr: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="h6" sx={{ whiteSpace: 'nowrap' }}>WordPress Publishing</Typography>
          <Divider orientation="vertical" flexItem />
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {summaryChips.map(chip => (
              <Chip
                key={chip.label}
                size="small"
                variant="outlined"
                color={chip.warn ? 'warning' : 'default'}
                label={<><Typography component="span" variant="caption" color="text.secondary">{chip.label}</Typography>{' '}<Typography component="span" variant="caption" fontWeight={600}>{chip.value}</Typography></>}
                sx={{ borderRadius: 1 }}
              />
            ))}
          </Stack>
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<FactCheckOutlinedIcon fontSize="small" />}
            onClick={() => setFieldCheckDialogOpen(true)}
            sx={{ textTransform: 'none' }}
          >
            Fältkontroll
          </Button>
          <IconButton
            size="small"
            onClick={refreshCurrentTab}
            disabled={itemsLoading || matchingLoading || summaryLoading}
            title="Uppdatera lista"
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={async () => {
              setSettingsDialogOpen(true);
              await refreshSettings();
            }}
            disabled={settingsLoading}
            title="Inställningar"
          >
            <SettingsIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      {message && (
        <Alert severity={message.severity} onClose={() => setMessage(null)} sx={{ py: 0.25 }}>
          {message.text}
        </Alert>
      )}

      {isPublicationTab && activeTabResolution?.source === 'missing' && (
        <Alert severity="warning" sx={{ py: 0.25 }}>
          Ingen rotkategori vald för {publicationTypeLabel(publicationTab || 'dvh')}. Öppna inställningar och välj vilken huvudkategori som ska styra denna flik.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, px: 1.5, pt: 0.5 }}>
          <Tabs
            value={activeTab}
            onChange={(_event, nextValue: WordPressPublishingTab) => {
              setActiveTab(nextValue);
              storeSelection(nextValue, null);
            }}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ minHeight: 40, '& .MuiTab-root': { minHeight: 40, py: 0.75, textTransform: 'none', overflow: 'visible' } }}
          >
            <Tab value="dvh" label={renderPublicationTabLabel('dvh', 'DVH')} />
            <Tab value="foodservice" label={renderPublicationTabLabel('foodservice', 'Food Service')} />
            <Tab value="wine" label={renderPublicationTabLabel('wine', 'Vin')} />
            <Tab value="recipe" label={renderPublicationTabLabel('recipe', 'Recept')} />
            <Tab value="quote-backgrounds" label="Quote bakgrundsbilder" />
            <Tab value="winery-images" label="Winery images" />
            <Tab value="matching" label="Matchning" />
            <Tab value="sync-warnings" label={syncWarningsTabLabel} />
          </Tabs>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ pb: 0.5 }}>
            {isPublicationTab ? (
              <>
                <Chip
                  size="small"
                  label="Behöver bild"
                  clickable
                  color={statusFilter === 'needs_image' ? 'primary' : 'default'}
                  variant={statusFilter === 'needs_image' ? 'filled' : 'outlined'}
                  onClick={() => setStatusFilter(current => current === 'needs_image' ? '' : 'needs_image')}
                />
                <Chip
                  size="small"
                  label="Klara att publicera"
                  clickable
                  color={statusFilter === 'ready_to_publish' ? 'primary' : 'default'}
                  variant={statusFilter === 'ready_to_publish' ? 'filled' : 'outlined'}
                  onClick={() => setStatusFilter(current => current === 'ready_to_publish' ? '' : 'ready_to_publish')}
                />
                <Chip
                  size="small"
                  label="Ändringar väntar"
                  clickable
                  color={statusFilter === 'changes_pending' ? 'primary' : 'default'}
                  variant={statusFilter === 'changes_pending' ? 'filled' : 'outlined'}
                  onClick={() => setStatusFilter(current => current === 'changes_pending' ? '' : 'changes_pending')}
                />
                <Chip
                  size="small"
                  label="Publicerade"
                  clickable
                  color={statusFilter === 'published' ? 'primary' : 'default'}
                  variant={statusFilter === 'published' ? 'filled' : 'outlined'}
                  onClick={() => setStatusFilter(current => current === 'published' ? '' : 'published')}
                />
                <Chip
                  size="small"
                  label="Fel"
                  clickable
                  color={statusFilter === 'error' ? 'primary' : 'default'}
                  variant={statusFilter === 'error' ? 'filled' : 'outlined'}
                  onClick={() => setStatusFilter(current => current === 'error' ? '' : 'error')}
                />
                <Chip
                  size="small"
                  label="Saknar fält"
                  clickable
                  color={fieldCheckFilter === 'missing' ? 'primary' : 'default'}
                  variant={fieldCheckFilter === 'missing' ? 'filled' : 'outlined'}
                  onClick={() => setFieldCheckFilter(current => current === 'missing' ? '' : 'missing')}
                />
                <Chip
                  size="small"
                  label="Synkvarningar"
                  clickable
                  color={syncWarningsFilter ? 'primary' : 'default'}
                  variant={syncWarningsFilter ? 'filled' : 'outlined'}
                  onClick={() => setSyncWarningsFilter(current => !current)}
                />
              </>
            ) : isMatchingTab ? (
              <Chip
                size="small"
                label="Utan koppling"
                clickable
                color={onlyUnlinked ? 'primary' : 'default'}
                variant={onlyUnlinked ? 'filled' : 'outlined'}
                onClick={() => setOnlyUnlinked(value => !value)}
              />
            ) : null}
            {hasActiveFilters && (
              <Chip
                size="small"
                label="Rensa filter"
                clickable
                variant="outlined"
                onClick={resetFilters}
              />
            )}
          </Stack>
        </Box>
        <Divider />

        {isQuoteBackgroundTab ? (
          <QuoteBackgroundsTab
            onMessage={setMessage}
            onChangeComplete={refreshSummary}
          />
        ) : isWineryImagesTab ? (
          <WineryImagesTab
            onMessage={setMessage}
            onChangeComplete={refreshSummary}
          />
        ) : isSyncWarningsTab ? (
          <MissingInWordPressView
            reloadKey={syncWarningsReloadKey}
            onMessage={setMessage}
            onChangeComplete={refreshSummary}
            onOpenAsset={openSyncWarningAsset}
          />
        ) : (
        <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1, flex: 1, minHeight: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <TextField
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder={isMatchingTab ? 'Sök legacy-recept eller original' : 'Sök asset, titel eller filnamn'}
              size="small"
              sx={{ flex: '1 1 280px', minWidth: { xs: '100%', sm: 280 } }}
              InputProps={{
                startAdornment: <SearchIcon fontSize="small" style={{ marginRight: 8, opacity: 0.65 }} />,
              }}
            />

            {isPublicationTab && (
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <Select
                  displayEmpty
                  value={statusFilter}
                  onChange={event => setStatusFilter(String(event.target.value || '') as WordPressUiStatus | '')}
                >
                  {statusOptions.map(option => (
                    <MenuItem key={option.value || 'all'} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {isPublicationTab && (
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <Select
                  value={itemsLimit}
                  onChange={event => setItemsLimit(Number(event.target.value) || DEFAULT_PUBLICATION_LIST_LIMIT)}
                >
                  {PUBLICATION_LIST_LIMIT_OPTIONS.map(limit => (
                    <MenuItem key={limit} value={limit}>
                      {limit} poster
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Stack>

          <Box
            ref={layoutRef}
            sx={{ display: 'flex', gap: { xs: 1.5, xl: 0 }, flexDirection: { xs: 'column', xl: 'row' }, flex: 1, minHeight: 0 }}
          >
            <Paper
              variant="outlined"
              sx={{
                flex: { xs: '1 1 auto', xl: '0 0 auto' },
                width: {
                  xs: '100%',
                  xl: leftPanelWidth !== null
                    ? `min(${leftPanelWidth}px, calc(100% - ${MIN_RIGHT_PANEL_WIDTH + RESIZE_HANDLE_WIDTH}px))`
                    : `${Math.round(DEFAULT_LEFT_PANEL_WIDTH_RATIO * 100)}%`,
                },
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
              }}
            >
              <Box sx={{ p: 1.25, pb: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 0.75 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {activeTab !== 'matching' && (
                      <Typography variant="subtitle1">WordPress-poster</Typography>
                    )}
                    {activeTab !== 'matching' && (
                      <FormControlLabel
                        sx={{ m: 0 }}
                        control={
                          <Checkbox
                            size="small"
                            checked={allVisibleSelected}
                            indeterminate={!allVisibleSelected && someVisibleSelected}
                            onChange={event => toggleSelectAllVisible(event.target.checked)}
                          />
                        }
                        label={
                          <Typography variant="body2">
                            {selectedBulkAssetIds.length > 0
                              ? `${selectedBulkAssetIds.length} / ${visibleItemIds.length}`
                              : itemsTotal > visibleItemIds.length
                                ? `${visibleItemIds.length} av ${itemsTotal} poster`
                                : `${visibleItemIds.length} poster`}
                          </Typography>
                        }
                      />
                    )}
                    {activeTab === 'matching' && (
                      <Typography variant="body2">
                        {matchingTotal > matchingItems.length
                          ? `Visar ${matchingItems.length} av ${matchingTotal} legacy-recept`
                          : `${matchingTotal} legacy-recept`}
                      </Typography>
                    )}
                  </Stack>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    {activeTab !== 'matching' && selectedBulkAssetIds.length > 0 && (
                      <>
                        <Button size="small" variant="outlined" onClick={() => handleBulkAction('enable')} disabled={!!busyAction}>
                          Välj för WordPress
                        </Button>
                        <Button size="small" variant="contained" onClick={() => handleBulkAction('generate')} disabled={!!busyAction}>
                          Skapa WP-bilder
                        </Button>
                        <Button size="small" variant="outlined" onClick={() => handleBulkAction('publish')} disabled={!!busyAction}>
                          Publicera
                        </Button>
                        <Button size="small" variant="outlined" color="warning" onClick={() => handleBulkAction('unpublish')} disabled={!!busyAction}>
                          Avpublicera från WordPress
                        </Button>
                        <Button size="small" variant="outlined" color="error" onClick={() => setBulkResetDialogOpen(true)} disabled={!!busyAction}>
                          Nollställ
                        </Button>
                        <Button size="small" onClick={() => setSelectedBulkAssetIds([])} disabled={!!busyAction}>
                          Avmarkera
                        </Button>
                      </>
                    )}
                    {activeTab === 'matching' && (
                      <Button
                        size="small"
                        startIcon={<AutorenewIcon />}
                        onClick={handleReindexMatching}
                        disabled={busyAction === 'matching-reindex'}
                      >
                        Uppdatera matchindex
                      </Button>
                    )}
                  </Stack>
                </Box>
                {activeTab === 'recipe' && selectedBulkAssetIds.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Bulk "Skapa WP-bilder" använder sparad beskärning om den finns, annars standardcrop.
                  </Typography>
                )}
              </Box>
              <Divider />

              <Stack spacing={0.75} sx={{ p: 1.25, overflowY: 'auto', flex: 1, minHeight: 0 }}>
                {activeTab !== 'matching' && itemsLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                  </Box>
                )}
                {activeTab === 'matching' && matchingLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                  </Box>
                )}

                {activeTab !== 'matching' && !itemsLoading && items.length === 0 && (
                  <Alert severity="info">Inga poster matchade filtret.</Alert>
                )}
                {activeTab === 'matching' && !matchingLoading && matchingItems.length === 0 && (
                  <Alert severity="info">Inga legacy-recept matchade filtret.</Alert>
                )}

                {activeTab !== 'matching' && items.map(item => {
                  const selected = item.asset._id === selectedAssetId;
                  const selectedForBulk = selectedBulkAssetIds.includes(item.asset._id);
                  return (
                    <Box
                      key={item.asset._id}
                      onClick={() => {
                        setSelectedAssetId(item.asset._id);
                        storeSelection(activeTab, item.asset._id);
                      }}
                      sx={{
                        border: '1px solid',
                        borderColor: selected ? 'primary.main' : 'divider',
                        borderRadius: 1,
                        p: 1.25,
                        cursor: 'pointer',
                        backgroundColor: selected ? 'action.selected' : 'background.paper',
                      }}
                    >
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 72px 1fr auto', gap: 1.5, alignItems: 'center' }}>
                        <Checkbox
                          checked={selectedForBulk}
                          onClick={event => event.stopPropagation()}
                          onChange={event => toggleBulkSelection(item.asset._id, event.target.checked)}
                          sx={{ alignSelf: 'flex-start', mt: -0.5 }}
                        />
                        <Box sx={{ width: 72, height: 72, borderRadius: 1, overflow: 'hidden', bgcolor: 'common.white', border: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 0.5 }}>
                          {item.asset.previewUrl ? (
                            <Box component="img" src={toAbsoluteUrl(item.asset.previewUrl)} alt={item.asset.title} sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                          ) : (
                            <ImageIcon color="disabled" />
                          )}
                        </Box>
                        <Stack spacing={0.75} minWidth={0}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                            <Typography variant="subtitle2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {item.asset.title}
                            </Typography>
                            {item.asset.identifier && (
                              <Typography variant="caption" color="text.secondary">
                                {item.asset.identifier}
                              </Typography>
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {item.asset.fileName}
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={0.5}
                            sx={{
                              minWidth: 0,
                              flexWrap: 'nowrap',
                              overflow: 'hidden',
                              alignItems: 'center',
                            }}
                          >
                            <Chip
                              size="small"
                              label={getWordPressUiStatusLabel(getWordPressUiStatusFromItem(item))}
                              color={getWordPressUiStatusColor(getWordPressUiStatusFromItem(item))}
                              sx={LIST_STATUS_CHIP_SX}
                            />
                            <FieldCheckListChip result={item.fieldCheck} sx={LIST_STATUS_CHIP_SX} />
                            <SyncWarningsListChip publication={item.publication} sx={LIST_STATUS_CHIP_SX} />
                            {item.publication?.pendingImageChange && (
                              <Chip
                                size="small"
                                label="Ny bildversion"
                                color="warning"
                                variant="outlined"
                                sx={LIST_STATUS_CHIP_SX}
                              />
                            )}
                            {item.publication?.pendingContentChange && (
                              <Chip
                                size="small"
                                label="Text ändrad"
                                color="info"
                                variant="outlined"
                                sx={LIST_STATUS_CHIP_SX}
                              />
                            )}
                            {item.contentType === 'product' && item.publication?.pendingQuoteBackgroundChange && (
                              <Chip
                                size="small"
                                label="Quote bakgrundsbild ändrad"
                                color="warning"
                                variant="outlined"
                                sx={LIST_STATUS_CHIP_SX}
                              />
                            )}
                            {item.contentType === 'wine' && item.publication?.pendingWineryImageChange && (
                              <Chip
                                size="small"
                                label="Winery image ändrad"
                                color="warning"
                                variant="outlined"
                                sx={LIST_STATUS_CHIP_SX}
                              />
                            )}
                            {item.contentType === 'product' && item.quoteBackground?.usesFallback && (
                              <Chip
                                size="small"
                                label="Fallback används"
                                color="info"
                                variant="outlined"
                                sx={LIST_STATUS_CHIP_SX}
                              />
                            )}
                            {item.contentType === 'wine' && item.wineryImage?.usesFallback && (
                              <Chip
                                size="small"
                                label="Fallback winery image"
                                color="info"
                                variant="outlined"
                                sx={LIST_STATUS_CHIP_SX}
                              />
                            )}
                            {item.contentType === 'product' && item.quoteBackground?.missing && (
                              <Chip
                                size="small"
                                label="Quote bakgrundsbild saknas"
                                color="default"
                                variant="outlined"
                                sx={LIST_STATUS_CHIP_SX}
                              />
                            )}
                            {item.contentType === 'wine' && item.wineryImage?.missing && (
                              <Chip
                                size="small"
                                label="Winery image saknas"
                                color="default"
                                variant="outlined"
                                sx={LIST_STATUS_CHIP_SX}
                              />
                            )}
                          </Stack>
                        </Stack>
                        <IconButton
                          size="small"
                          onClick={event => {
                            event.stopPropagation();
                            openAssetDetailsPanel(item.asset._id);
                          }}
                          title="Öppna assetdetaljer"
                          sx={{ alignSelf: 'flex-start' }}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  );
                })}

                {activeTab === 'matching' && matchingItems.map(item => {
                  const selected = item.legacyAsset._id === selectedLegacyAssetId;
                  return (
                    <Box
                      key={item.legacyAsset._id}
                      onClick={() => {
                        setSelectedLegacyAssetId(item.legacyAsset._id);
                        storeSelection('matching', item.legacyAsset._id);
                      }}
                      sx={{
                        border: '1px solid',
                        borderColor: selected ? 'primary.main' : 'divider',
                        borderRadius: 1,
                        p: 1.25,
                        cursor: 'pointer',
                        backgroundColor: selected ? 'action.selected' : 'background.paper',
                      }}
                    >
                      <Box sx={{ display: 'grid', gridTemplateColumns: '72px 1fr auto', gap: 1.5 }}>
                        <Box sx={{ width: 72, height: 72, borderRadius: 1, overflow: 'hidden', bgcolor: 'common.white', border: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 0.5 }}>
                          {item.legacyAsset.previewUrl ? (
                            <Box component="img" src={toAbsoluteUrl(item.legacyAsset.previewUrl)} alt={item.legacyAsset.title} sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                          ) : (
                            <ImageIcon color="disabled" />
                          )}
                        </Box>
                        <Stack spacing={0.75} minWidth={0}>
                          <Typography variant="subtitle2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {item.legacyAsset.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.legacyAsset.fileName}
                          </Typography>
                          <Stack direction="row" spacing={0.75} flexWrap="wrap">
                            <Chip
                              size="small"
                              label={item.associationStatus === 'linked' ? 'Kopplad' : 'Saknar koppling'}
                              color={item.associationStatus === 'linked' ? 'success' : 'warning'}
                            />
                            {item.linkedSourceAssetId && (
                              <Chip size="small" variant="outlined" label={`Original: ${item.linkedSourceAssetId}`} />
                            )}
                          </Stack>
                        </Stack>
                        <IconButton
                          size="small"
                          onClick={event => {
                            event.stopPropagation();
                            openAssetDetailsPanel(item.legacyAsset._id);
                          }}
                          title="Öppna assetdetaljer"
                          sx={{ alignSelf: 'flex-start' }}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            </Paper>

            <Box
              role="separator"
              aria-orientation="vertical"
              aria-label="Ändra panelbredd"
              tabIndex={isWideLayout ? 0 : -1}
              onMouseDown={() => {
                if (isWideLayout) setIsResizingPanels(true);
              }}
              onDoubleClick={() => setLeftPanelWidth(null)}
              onKeyDown={(event) => {
                if (event.key === 'ArrowLeft') {
                  event.preventDefault();
                  adjustPaneWidth(-40);
                } else if (event.key === 'ArrowRight') {
                  event.preventDefault();
                  adjustPaneWidth(40);
                }
              }}
              sx={{
                display: { xs: 'none', xl: 'flex' },
                alignItems: 'stretch',
                justifyContent: 'center',
                cursor: 'col-resize',
                mx: 0.75,
                my: 0.25,
                py: 0.5,
                borderRadius: 1,
                backgroundColor: isResizingPanels ? 'action.hover' : 'transparent',
                '&:hover': { backgroundColor: 'action.hover' },
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: 1,
                },
              }}
            >
              <Box
                sx={{
                  width: '2px',
                  borderRadius: '999px',
                  backgroundColor: isResizingPanels ? 'primary.main' : 'divider',
                }}
              />
            </Box>

            <Paper variant="outlined" sx={{ flex: 1, p: 2, overflowY: 'auto', minHeight: 0, minWidth: 0 }}>
              {activeTab !== 'matching' && selectedItem && (
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start' }}>
                    <Box>
                      <Typography variant="h6">{selectedItem.asset.title}</Typography>
                      <Typography variant="body2" color="text.secondary">{selectedItem.asset.fileName}</Typography>
                    </Box>
                    <Chip label={getWordPressUiStatusLabel(selectedItemUiStatus)} color={getWordPressUiStatusColor(selectedItemUiStatus)} />
                  </Box>

                  <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
                    <Stack spacing={1.25}>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={selectedItem.publication?.enabled ?? false}
                              onChange={event => handleEnableChange(selectedItem, event.target.checked)}
                            />
                          }
                          label="Publicera i WordPress"
                        />
                        <Chip size="small" label={getWordPressUiStatusLabel(selectedItemUiStatus)} color={getWordPressUiStatusColor(selectedItemUiStatus)} />
                          {selectedItemHasPendingQuoteBackgroundChange && (
                            <Chip size="small" label="Quote bakgrundsbild ändrad" color="warning" variant="outlined" />
                          )}
                          {selectedItemHasPendingWineryImageChange && (
                            <Chip size="small" label="Winery image ändrad" color="warning" variant="outlined" />
                          )}
                          {selectedItemUsesQuoteBackground && selectedQuoteBackground?.usesFallback && (
                            <Chip size="small" label="Fallback används" color="info" variant="outlined" />
                          )}
                          {selectedItemUsesWineryImage && selectedWineryImage?.usesFallback && (
                            <Chip size="small" label="Fallback winery image" color="info" variant="outlined" />
                          )}
                          {selectedItemUsesQuoteBackground && selectedQuoteBackground?.missing && (
                            <Chip size="small" label="Quote bakgrundsbild saknas" color="default" variant="outlined" />
                          )}
                          {selectedItemUsesWineryImage && selectedWineryImage?.missing && (
                            <Chip size="small" label="Winery image saknas" color="default" variant="outlined" />
                          )}
                          {selectedRecipePreservePending && (
                            <Chip size="small" label="Väntar på beskuren högupplöst receptbild" color="warning" variant="outlined" />
                          )}
                          {selectedRecipePreserveCompleted && (
                            <Chip size="small" label="Originalbild bevarad" color="success" variant="outlined" />
                          )}
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {getWordPressUiStatusDescription(selectedItemUiStatus)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Avpublicering sätter den länkade WordPress-posten till utkast. Posten raderas inte.
                      </Typography>
                    </Stack>
                    {selectedPendingAlert && (
                      <Alert severity={selectedPendingAlert.severity} sx={{ mt: 1.5 }}>
                        {selectedPendingAlert.text}
                      </Alert>
                    )}
                    {selectedItem.publication?.syncError && (
                      <Alert severity="error" sx={{ mt: 1.5 }}>
                        {selectedItem.publication.syncError}
                      </Alert>
                    )}
                  </Paper>

                  <FieldCheckCard
                    result={selectedItem.fieldCheck}
                    isPublished={selectedPublicationIsPublished}
                    rechecking={fieldCheckRechecking}
                    onOpenAsset={() => openAssetDetailsPanel(selectedItem.asset._id)}
                    onRecheck={handleRecheckSelectedItem}
                  />

                  <SyncWarningsCard publication={selectedItem.publication} />

                  {selectedItem.inferredType === 'recipe' ? (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.2fr) minmax(320px, 0.8fr)' }, gap: 2 }}>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        {selectedRecipePreservePending && (
                          <Alert severity="info" sx={{ mb: 1.5 }}>
                            Du beskär nu originalbilden. När du skapar bilden sparas både WordPress-variant och en ny högupplöst receptbild, medan originalet behålls oförändrat.
                          </Alert>
                        )}
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>Beskärning 796x530</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                          Dra i bilden för att flytta fokus. Zooma sedan in tills receptbilden sitter rätt.
                        </Typography>

                        <Box
                          onPointerDown={updateFocusFromPointer}
                          onPointerMove={event => {
                            if (event.buttons === 1) updateFocusFromPointer(event);
                          }}
                          sx={{
                            position: 'relative',
                            width: '100%',
                            aspectRatio: `${RECIPE_TARGET.width} / ${RECIPE_TARGET.height}`,
                            overflow: 'hidden',
                            borderRadius: 1,
                            background: 'linear-gradient(135deg, #eceff1, #f8fafc)',
                            border: '1px solid',
                            borderColor: 'divider',
                            cursor: 'grab',
                          }}
                        >
                          {recipeEditorPreviewUrl ? (
                            <Box
                              sx={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <Box
                                component="img"
                                src={recipeEditorPreviewUrl}
                                alt={selectedItem.asset.title}
                                onLoad={event => {
                                  const element = event.currentTarget;
                                  if (element.naturalWidth > 0 && element.naturalHeight > 0) {
                                    setRecipeNaturalSize({
                                      width: element.naturalWidth,
                                      height: element.naturalHeight,
                                    });
                                  }
                                }}
                                sx={{
                                  position: 'absolute',
                                  width: recipePreviewPlacement.width,
                                  height: recipePreviewPlacement.height,
                                  left: recipePreviewPlacement.left,
                                  top: recipePreviewPlacement.top,
                                  objectFit: 'contain',
                                  objectPosition: 'center center',
                                  userSelect: 'none',
                                  pointerEvents: 'none',
                                }}
                              />
                            </Box>
                          ) : (
                            <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', color: 'text.secondary' }}>
                              <ImageIcon />
                              <Typography variant="body2">Ingen preview tillgänglig</Typography>
                            </Stack>
                          )}
                          <Box sx={{ position: 'absolute', inset: 0, border: '2px solid rgba(255,255,255,0.75)', boxShadow: 'inset 0 0 0 1px rgba(7,47,83,0.28)' }} />
                        </Box>

                        <Stack spacing={1.5} sx={{ mt: 2 }}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Zoom</Typography>
                            <Slider min={1} max={5} step={0.05} value={zoom} onChange={(_event, nextValue) => setZoom(Number(nextValue))} />
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Horisontell position</Typography>
                            <Slider min={0} max={100} step={1} value={focusX} onChange={(_event, nextValue) => setFocusX(Number(nextValue))} />
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Vertikal position</Typography>
                            <Slider min={0} max={100} step={1} value={focusY} onChange={(_event, nextValue) => setFocusY(Number(nextValue))} />
                          </Box>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ pt: 0.5 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<Rotate90DegreesCwOutlinedIcon />}
                              onClick={handleRecipeRotate90}
                              disabled={!!busyAction}
                            >
                              Rotera 90°
                            </Button>
                            <Button
                              size="small"
                              variant={recipeFlipHorizontal ? 'contained' : 'outlined'}
                              startIcon={<FlipIcon />}
                              onClick={() => setRecipeFlipHorizontal(value => !value)}
                              disabled={!!busyAction}
                            >
                              Spegelvänd
                            </Button>
                          </Stack>
                        </Stack>
                      </Paper>

                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>WordPress-variant</Typography>
                        <Stack spacing={1.5}>
                          {canGenerateSelectedItem && (
                            <Button
                              variant={selectedItemUiStatus === 'needs_image' || selectedItemHasPendingImageChange ? 'contained' : 'outlined'}
                              startIcon={<ImageIcon />}
                              onClick={() => handleGenerate(selectedItem)}
                              disabled={!!busyAction}
                            >
                              {selectedRecipePreservePending
                                ? 'Skapa WP-bild och receptbild'
                                : selectedItemHasPendingImageChange
                                  ? 'Skapa ny WP-bild'
                                  : 'Skapa WP-bild'}
                            </Button>
                          )}
                          {canRequestSyncSelectedItem && (
                            <Button
                              variant="contained"
                              startIcon={<SyncIcon />}
                              onClick={() => handleRequestSync(selectedItem)}
                              disabled={!selectedItem.publication?.rendition || !!busyAction || selectedFieldCheckBlocked}
                            >
                              Publicera ändringar
                            </Button>
                          )}
                          {canPublishSelectedItem && (
                            <Button
                              variant="contained"
                              startIcon={selectedItemUiStatus === 'error' ? <SyncIcon /> : <CheckCircleIcon />}
                              onClick={() => handlePublish(selectedItem)}
                              disabled={!selectedItem.publication?.rendition || !!busyAction || selectedFieldCheckBlocked}
                            >
                              {selectedItemUiStatus === 'error' || selectedItemUiStatus === 'unpublished' ? 'Publicera igen' : 'Publicera'}
                            </Button>
                          )}
                          {(canPublishSelectedItem || canRequestSyncSelectedItem) && (
                            <SyncWarningsPublishNotice publication={selectedItem.publication} />
                          )}
                          {canUnpublishSelectedItem && (
                            <Button
                              variant="outlined"
                              color="warning"
                              startIcon={<SyncIcon />}
                              onClick={() => handleUnpublish(selectedItem)}
                              disabled={!!busyAction}
                            >
                              Avpublicera från WordPress
                            </Button>
                          )}
                          {(selectedItemUiStatus === 'publishing' || selectedItemUiStatus === 'unpublishing') && (
                            <Button variant="outlined" disabled startIcon={<SyncIcon />}>
                              {selectedItemUiStatus === 'unpublishing' ? 'Avpubliceras' : 'Publiceras'}
                            </Button>
                          )}
                          <Divider />
                          <Typography variant="body2" color="text.secondary">
                            Senaste sparade WordPress-version
                          </Typography>
                          <Box
                            onClick={selectedRenditionUrl ? openRenditionPreview : undefined}
                            onKeyDown={selectedRenditionUrl ? handleRenditionPreviewKeyDown : undefined}
                            role={selectedRenditionUrl ? 'button' : undefined}
                            tabIndex={selectedRenditionUrl ? 0 : -1}
                            aria-label={selectedRenditionUrl ? 'Visa WordPress-variant i naturlig storlek' : undefined}
                            sx={{
                              width: '100%',
                              aspectRatio: `${RECIPE_TARGET.width} / ${RECIPE_TARGET.height}`,
                              borderRadius: 1,
                              overflow: 'hidden',
                              bgcolor: 'grey.100',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: selectedRenditionUrl ? 'zoom-in' : 'default',
                            }}
                          >
                            {selectedRenditionUrl ? (
                              <Box component="img" src={selectedRenditionUrl} alt="WordPress recipe rendition" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              <Typography variant="body2" color="text.secondary">Ingen sparad WP-bild ännu</Typography>
                            )}
                          </Box>
                        </Stack>
                      </Paper>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) minmax(320px, 0.9fr)' }, gap: 2 }}>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>Original</Typography>
                        <Box sx={{ width: '100%', minHeight: 320, maxHeight: ORIGINAL_PREVIEW_MAX_HEIGHT, borderRadius: 1, overflow: 'hidden', bgcolor: 'grey.100', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {selectedPreviewUrl ? (
                            <Box
                              component="img"
                              src={selectedPreviewUrl}
                              alt={selectedItem.asset.title}
                              sx={{ maxWidth: '100%', maxHeight: ORIGINAL_PREVIEW_MAX_HEIGHT, width: 'auto', height: 'auto', objectFit: 'contain', display: 'block' }}
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">Ingen preview tillgänglig</Typography>
                          )}
                        </Box>

                        {selectedItem.contentType === 'product' && (
                          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="subtitle1" sx={{ mb: 0.75 }}>Quote bakgrundsbild</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {selectedQuoteBackground?.selectedLabel
                                ? `Valt värde: ${selectedQuoteBackground.selectedLabel}`
                                : 'Produkten saknar valt quote-värde och använder fallback om en sådan finns.'}
                            </Typography>
                            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                              {selectedQuoteBackground?.resolvedLabel && (
                                <Chip size="small" label={`Banner: ${selectedQuoteBackground.resolvedLabel}`} />
                              )}
                              {selectedQuoteBackground?.usesFallback && (
                                <Chip size="small" label="Fallback används" color="info" variant="outlined" />
                              )}
                              {selectedQuoteBackground?.missing && (
                                <Chip size="small" label="Quote bakgrundsbild saknas" color="default" variant="outlined" />
                              )}
                            </Stack>
                            <Box
                              sx={{
                                mt: 1.5,
                                width: '100%',
                                aspectRatio: '1225 / 406',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                bgcolor: 'grey.50',
                                overflow: 'hidden',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              {selectedQuoteBackgroundUrl ? (
                                <Box component="img" src={selectedQuoteBackgroundUrl} alt={selectedQuoteBackground?.title || selectedItem.asset.title} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              ) : (
                                <Typography variant="body2" color="text.secondary">Ingen quote-banner löst ännu</Typography>
                              )}
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              {selectedQuoteBackground?.sourceAssetUpdated
                                ? 'Källbilden för bannern har uppdaterats och behöver genereras om i Quote bakgrundsbilder-fliken.'
                                : 'Previewn visar den banner som WordPress-pluginen kommer att få för den här produkten.'}
                            </Typography>
                          </Box>
                        )}

                        {selectedItem.contentType === 'wine' && (
                          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="subtitle1" sx={{ mb: 0.75 }}>Winery image</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {selectedWineryImage?.selectedLabel
                                ? `Producent: ${selectedWineryImage.selectedLabel}`
                                : 'Vinet saknar producent i producent_id och använder fallback om en sådan finns.'}
                            </Typography>
                            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                              {selectedWineryImage?.usesFallback && (
                                <Chip size="small" label="Fallback winery image" color="info" variant="outlined" />
                              )}
                              {selectedWineryImage?.missing && (
                                <Chip size="small" label="Winery image saknas" color="default" variant="outlined" />
                              )}
                              {selectedWineryImage?.sourceAssetUpdated && (
                                <Chip size="small" label="Källbild uppdaterad" color="warning" variant="outlined" />
                              )}
                            </Stack>
                            <Box
                              sx={{
                                mt: 1.5,
                                width: '100%',
                                aspectRatio: '1200 / 810',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                bgcolor: 'grey.50',
                                overflow: 'hidden',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              {selectedWineryImageUrl ? (
                                <Box component="img" src={selectedWineryImageUrl} alt={selectedWineryImage?.title || selectedItem.asset.title} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              ) : (
                                <Typography variant="body2" color="text.secondary">Ingen winery image löst ännu</Typography>
                              )}
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              {selectedWineryImage?.sourceAssetUpdated
                                ? 'Källbilden för winery image har uppdaterats och behöver genereras om i Winery images-fliken.'
                                : 'Previewn visar den winery image som WordPress-pluginen kommer att få för det här vinet.'}
                            </Typography>
                          </Box>
                        )}
                      </Paper>

                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>WordPress-variant</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {(selectedItem.inferredType === 'dvh' || selectedItem.inferredType === 'foodservice')
                            ? 'Skapar 1800x1440 WebP med vit bakgrund.'
                            : 'Skapar 1200x1400 WebP med vit bakgrund.'}
                        </Typography>
                        <Stack spacing={1.5}>
                          {canGenerateSelectedItem && (
                            <Button
                              variant={selectedItemUiStatus === 'needs_image' || selectedItemHasPendingImageChange ? 'contained' : 'outlined'}
                              startIcon={<ImageIcon />}
                              onClick={() => handleGenerate(selectedItem)}
                              disabled={!!busyAction}
                            >
                              {selectedItemHasPendingImageChange ? 'Skapa ny WP-bild' : 'Skapa WP-bild'}
                            </Button>
                          )}
                          {canRequestSyncSelectedItem && (
                            <Button
                              variant="contained"
                              startIcon={<SyncIcon />}
                              onClick={() => handleRequestSync(selectedItem)}
                              disabled={!selectedItem.publication?.rendition || !!busyAction || selectedFieldCheckBlocked}
                            >
                              Publicera ändringar
                            </Button>
                          )}
                          {canPublishSelectedItem && (
                            <Button
                              variant="contained"
                              startIcon={selectedItemUiStatus === 'error' ? <SyncIcon /> : <CheckCircleIcon />}
                              onClick={() => handlePublish(selectedItem)}
                              disabled={!selectedItem.publication?.rendition || !!busyAction || selectedFieldCheckBlocked}
                            >
                              {selectedItemUiStatus === 'error' || selectedItemUiStatus === 'unpublished' ? 'Publicera igen' : 'Publicera'}
                            </Button>
                          )}
                          {(canPublishSelectedItem || canRequestSyncSelectedItem) && (
                            <SyncWarningsPublishNotice publication={selectedItem.publication} />
                          )}
                          {canUnpublishSelectedItem && (
                            <Button
                              variant="outlined"
                              color="warning"
                              startIcon={<SyncIcon />}
                              onClick={() => handleUnpublish(selectedItem)}
                              disabled={!!busyAction}
                            >
                              Avpublicera från WordPress
                            </Button>
                          )}
                          {(selectedItemUiStatus === 'publishing' || selectedItemUiStatus === 'unpublishing') && (
                            <Button variant="outlined" disabled startIcon={<SyncIcon />}>
                              {selectedItemUiStatus === 'unpublishing' ? 'Avpubliceras' : 'Publiceras'}
                            </Button>
                          )}
                          <Divider />
                          <Box
                            onClick={selectedRenditionUrl ? openRenditionPreview : undefined}
                            onKeyDown={selectedRenditionUrl ? handleRenditionPreviewKeyDown : undefined}
                            role={selectedRenditionUrl ? 'button' : undefined}
                            tabIndex={selectedRenditionUrl ? 0 : -1}
                            aria-label={selectedRenditionUrl ? 'Visa WordPress-variant i naturlig storlek' : undefined}
                            sx={{
                              width: '100%',
                              minHeight: 320,
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'divider',
                              bgcolor: 'grey.50',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              p: 2,
                              cursor: selectedRenditionUrl ? 'zoom-in' : 'default',
                            }}
                          >
                            <Box
                              sx={{
                                width: '100%',
                                maxWidth: selectedItem.inferredType === 'wine' ? 320 : 420,
                                aspectRatio: getRenditionAspectRatio(selectedItem.inferredType),
                                borderRadius: 1,
                                bgcolor: 'grey.300',
                                boxShadow: 'inset 0 0 0 1px rgba(7,47,83,0.12)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                p: 1.5,
                                overflow: 'hidden',
                              }}
                            >
                              {selectedRenditionUrl ? (
                                <Box component="img" src={selectedRenditionUrl} alt="WordPress rendition" sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
                              ) : (
                                <Typography variant="body2" color="text.secondary">Ingen sparad WP-bild ännu</Typography>
                              )}
                            </Box>
                          </Box>
                        </Stack>
                      </Paper>
                    </Box>
                  )}
                </Stack>
              )}

              {activeTab === 'matching' && selectedLegacyItem && (
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start' }}>
                    <Box>
                      <Typography variant="h6">{selectedLegacyItem.legacyAsset.title}</Typography>
                      <Typography variant="body2" color="text.secondary">{selectedLegacyItem.legacyAsset.fileName}</Typography>
                    </Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip
                        label={selectedLegacyItem.associationStatus === 'linked' ? 'Kopplad' : 'Saknar koppling'}
                        color={selectedLegacyItem.associationStatus === 'linked' ? 'success' : 'warning'}
                      />
                      <Chip
                        label={getWpVariantSourceLabel(selectedLegacyItem.currentVariantSource)}
                        color={getWpVariantSourceColor(selectedLegacyItem.currentVariantSource)}
                        variant={selectedLegacyItem.currentVariantSource ? 'filled' : 'outlined'}
                      />
                    </Stack>
                  </Box>

                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(260px, 0.7fr) minmax(0, 1.3fr)' }, gap: 2 }}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>Legacy-receptbild</Typography>
                      <Box sx={{ width: '100%', aspectRatio: `${RECIPE_TARGET.width} / ${RECIPE_TARGET.height}`, borderRadius: 1, overflow: 'hidden', bgcolor: 'common.white', border: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 1 }}>
                        {selectedLegacyItem.legacyAsset.previewUrl ? (
                          <Box component="img" src={toAbsoluteUrl(selectedLegacyItem.legacyAsset.previewUrl)} alt={selectedLegacyItem.legacyAsset.title} sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                        ) : (
                          <Typography variant="body2" color="text.secondary">Ingen preview tillgänglig</Typography>
                        )}
                      </Box>

                      <Stack spacing={0.5} sx={{ mt: 1.5 }}>
                        <Typography variant="body2">
                          <Box component="span" sx={{ fontWeight: 600 }}>Titel:</Box> {selectedLegacyItem.legacyAsset.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>Filnamn:</Box> {selectedLegacyItem.legacyAsset.fileName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>Upplösning:</Box> {formatAssetResolution(selectedLegacyItem.legacyAsset)}
                        </Typography>
                      </Stack>

                      <Button
                        sx={{ mt: 2 }}
                        variant="contained"
                        startIcon={<SearchIcon />}
                        onClick={() => handleLoadSuggestions(selectedLegacyItem.legacyAsset._id)}
                        disabled={suggestionsLoading || !!busyAction}
                      >
                        Hämta förslag
                      </Button>
                    </Paper>

                    <Paper variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
                      <FormControlLabel
                        control={(
                          <Switch
                            checked={preserveOriginalOnMatch}
                            onChange={event => setPreserveOriginalOnMatch(event.target.checked)}
                            disabled={!!busyAction}
                          />
                        )}
                        label="Behåll original och skapa beskuren receptbild"
                        sx={{ mb: 1 }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5 }}>
                        När aktiv hoppar flödet till Recept efter koppling. Originalbilden lämnas orörd och du skapar sedan både WP-bild och ny högupplöst receptbild via beskärningen.
                      </Typography>
                      <Tabs
                        value={matchingDetailTab}
                        onChange={(_, next) => setMatchingDetailTab(next)}
                        sx={{ borderBottom: 1, borderColor: 'divider', mb: 1.5 }}
                      >
                        <Tab value="suggestions" label="Förslag" />
                        <Tab value="search" label="Sök i DAM" />
                      </Tabs>

                      {matchingDetailTab === 'suggestions' && (
                        <Box>
                          <Typography variant="subtitle1" sx={{ mb: 1 }}>Förslag på högupplöst original</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Förslagen söks i hela DAM och vägs med near-duplicate-analys, titel, kategori och upplösning. Bekräfta alltid kopplingen manuellt.
                          </Typography>

                          {suggestionsLoading && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                              <CircularProgress />
                            </Box>
                          )}

                          {!suggestionsLoading && suggestions.length === 0 && (
                            <Alert severity="info">
                              Inga förslag hämtade ännu. Kör "Hämta förslag" eller uppdatera matchindex om du saknar kandidater.
                            </Alert>
                          )}

                          <Stack spacing={1.5}>
                            {suggestions.map(suggestion => (
                              <Box key={suggestion.asset._id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.25 }}>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '84px 1fr', gap: 1.5 }}>
                                  <MatchingAssetThumbnailHoverPreview
                                    previewUrl={suggestion.asset.previewUrl}
                                    alt={suggestion.asset.title}
                                    sx={{
                                      width: 84,
                                      height: 84,
                                      borderRadius: 1,
                                      overflow: 'hidden',
                                      bgcolor: 'common.white',
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      p: 0.5,
                                    }}
                                  >
                                    {suggestion.asset.previewUrl ? (
                                      <Box component="img" src={toAbsoluteUrl(suggestion.asset.previewUrl)} alt={suggestion.asset.title} sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                    ) : (
                                      <ImageIcon color="disabled" />
                                    )}
                                  </MatchingAssetThumbnailHoverPreview>
                                  <Stack spacing={0.75}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                      <Typography variant="subtitle2">Titel: {suggestion.asset.title}</Typography>
                                      <Chip size="small" color="info" label={`Poäng ${suggestion.score}`} />
                                    </Box>
                                    <Typography variant="body2" color="text.secondary">
                                      <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>Filnamn:</Box> {suggestion.asset.fileName}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>Upplösning:</Box> {formatAssetResolution(suggestion.asset)}
                                    </Typography>
                                    <Stack direction="row" spacing={0.75} flexWrap="wrap">
                                      {suggestion.reasons.map(reason => (
                                        <Chip key={reason} size="small" variant="outlined" label={reason} />
                                      ))}
                                    </Stack>
                                    <Button
                                      size="small"
                                      startIcon={<LinkIcon />}
                                      sx={{ alignSelf: 'flex-start', mt: 0.5 }}
                                      onClick={() => handleLinkLegacyAsset(suggestion.asset._id, selectedLegacyItem.legacyAsset._id)}
                                      disabled={!!busyAction}
                                    >
                                      Bekräfta koppling
                                    </Button>
                                  </Stack>
                                </Box>
                              </Box>
                            ))}
                          </Stack>
                        </Box>
                      )}

                      {matchingDetailTab === 'search' && (
                        <Stack spacing={1.25} sx={{ flex: 1, minHeight: 0 }}>
                          <Box>
                            <Typography variant="subtitle1" sx={{ mb: 0.5 }}>Sök bild manuellt i DAM</Typography>
                            <Typography variant="body2" color="text.secondary">
                              Om rätt högupplösta bild inte kommer upp i förslagen kan du söka fram den här och koppla den manuellt.
                            </Typography>
                          </Box>

                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                            <TextField
                              fullWidth
                              size="small"
                              placeholder="Sök på titel eller filnamn"
                              value={manualAssetSearch}
                              onChange={(event) => setManualAssetSearch(event.target.value)}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                  event.preventDefault();
                                  void handleManualAssetSearch();
                                }
                              }}
                            />
                            <Button
                              variant="outlined"
                              startIcon={<SearchIcon />}
                              onClick={() => void handleManualAssetSearch()}
                              disabled={manualAssetSearchLoading || !!busyAction || manualAssetSearch.trim().length < 2}
                            >
                              Sök i DAM
                            </Button>
                          </Stack>

                          {manualAssetSearchLoading && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                              <CircularProgress size={24} />
                            </Box>
                          )}

                          {!manualAssetSearchLoading && manualAssetSearchPerformed && manualAssetResults.length === 0 && (
                            <Alert severity="info">
                              Inga bildträffar hittades för den sökningen. Prova gärna annat filnamn, titel eller artikelnummer.
                            </Alert>
                          )}

                          {manualAssetResults.length > 0 && (
                            <Stack spacing={1} sx={{ maxHeight: { xs: 480, lg: 'min(72vh, 900px)' }, overflowY: 'auto', pr: 0.5 }}>
                              {manualAssetResults.map((asset) => (
                                <Box key={asset._id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.25 }}>
                                  <Box sx={{ display: 'grid', gridTemplateColumns: '72px 1fr', gap: 1.25 }}>
                                    <MatchingAssetThumbnailHoverPreview
                                      previewUrl={asset.previewUrl}
                                      alt={asset.title}
                                      sx={{
                                        width: 72,
                                        height: 72,
                                        borderRadius: 1,
                                        overflow: 'hidden',
                                        bgcolor: 'common.white',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        p: 0.5,
                                      }}
                                    >
                                      {asset.previewUrl ? (
                                        <Box component="img" src={toAbsoluteUrl(asset.previewUrl)} alt={asset.title} sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                      ) : (
                                        <ImageIcon color="disabled" />
                                      )}
                                    </MatchingAssetThumbnailHoverPreview>
                                    <Stack spacing={0.6}>
                                      <Typography variant="subtitle2">Titel: {asset.title}</Typography>
                                      <Typography variant="body2" color="text.secondary">
                                        <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>Filnamn:</Box> {asset.fileName}
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary">
                                        <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>Upplösning:</Box> {formatAssetResolution(asset)}
                                      </Typography>
                                      <Button
                                        size="small"
                                        startIcon={<LinkIcon />}
                                        sx={{ alignSelf: 'flex-start', mt: 0.5 }}
                                        onClick={() => handleLinkLegacyAsset(asset._id, selectedLegacyItem.legacyAsset._id)}
                                        disabled={!!busyAction}
                                      >
                                        Koppla vald bild
                                      </Button>
                                    </Stack>
                                  </Box>
                                </Box>
                              ))}
                            </Stack>
                          )}
                        </Stack>
                      )}
                    </Paper>
                  </Box>
                </Stack>
              )}

              {activeTab !== 'matching' && !selectedItem && !itemsLoading && (
                <Alert severity="info">Välj en post i listan för att arbeta vidare.</Alert>
              )}
              {activeTab === 'matching' && !selectedLegacyItem && !matchingLoading && (
                <Alert severity="info">Välj en legacy-receptbild i listan för att hämta förslag och bekräfta koppling.</Alert>
              )}
            </Paper>
          </Box>
        </Box>
        )}
      </Paper>

      <Dialog open={settingsDialogOpen} onClose={() => setSettingsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>WordPress-inställningar</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Valj vilken huvudkategori som ska styra respektive WordPress-flode. Det gor modulen robust i både dev och produktion utan att vara hardkodad till vissa kategorinamn.
            </Typography>

            {(['dvh', 'foodservice', 'wine', 'recipe'] as WordPressPublicationType[]).map(type => (
              <FormControl key={type} fullWidth size="small">
                <Typography variant="subtitle2" sx={{ mb: 0.75 }}>{publicationTypeLabel(type)}</Typography>
                <Select
                  value={categoryRootsDraft[type]}
                  onChange={event => setCategoryRootsDraft(previousDraft => ({
                    ...previousDraft,
                    [type]: String(event.target.value || ''),
                  }))}
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>Ingen vald</em>
                  </MenuItem>
                  {rootCategories.map(category => (
                    <MenuItem key={category._id} value={category._id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75 }}>
                  Aktiv upplosning: {getResolutionLabel(wordpressSettings?.resolutions?.[type])}
                </Typography>
              </FormControl>
            ))}

            {rootCategories.length === 0 && !settingsLoading && (
              <Alert severity="warning">
                Inga huvudkategorier hittades i dev just nu. Du kan fortfarande skapa dem i DAM och sen valja dem har.
              </Alert>
            )}

            <Divider />

            <Stack spacing={1.25}>
              <Typography variant="subtitle2" color="error">
                Nollställ WordPress-publicering
              </Typography>
              <Alert severity="warning">
                Nollställer endast Athenas WordPress Publishing-state. WordPress-poster/media raderas inte, och skapade WP-bilder, beskärningar och interna matchningar behålls.
              </Alert>
              <FormControl fullWidth size="small">
                <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Target</Typography>
                <Select
                  value={resetTarget}
                  onChange={event => setResetTarget(String(event.target.value || 'all') as WordPressResetTarget)}
                >
                  {resetTargetOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Bekräftelse"
                value={resetConfirmation}
                onChange={event => setResetConfirmation(event.target.value)}
                helperText={`Skriv ${WORDPRESS_PUBLISHING_RESET_CONFIRMATION} för att aktivera nollställning.`}
                fullWidth
              />
              <Button
                variant="outlined"
                color="error"
                onClick={handleGlobalReset}
                disabled={!resetConfirmationMatches || !!busyAction}
              >
                {busyAction === 'global-reset' ? 'Nollställer...' : 'Nollställ valda targets'}
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsDialogOpen(false)}>Stang</Button>
          <Button variant="contained" onClick={handleSaveSettings} disabled={settingsSaving}>
            {settingsSaving ? 'Sparar...' : 'Spara'}
          </Button>
        </DialogActions>
      </Dialog>

      <FieldCheckSettingsDialog
        open={fieldCheckDialogOpen}
        initialTarget={publicationTab}
        onClose={() => setFieldCheckDialogOpen(false)}
        onSaved={() => { void refreshCurrentTab(); }}
      />

      <FieldCheckPublishDialog
        open={Boolean(publishConfirm)}
        itemTitle={publishConfirm?.item.asset.title || ''}
        result={publishConfirm?.item.fieldCheck}
        busy={!!busyAction}
        onCancel={() => setPublishConfirm(null)}
        onOpenAsset={() => {
          if (!publishConfirm) return;
          const assetId = publishConfirm.item.asset._id;
          setPublishConfirm(null);
          openAssetDetailsPanel(assetId);
        }}
        onConfirm={() => { void handlePublishConfirmed(); }}
      />

      <FieldCheckBulkPublishDialog
        open={Boolean(bulkPublishSummary)}
        summary={bulkPublishSummary}
        busy={!!busyAction}
        onCancel={() => setBulkPublishSummary(null)}
        onPublishAllowed={() => { void handleBulkPublishConfirmed(false); }}
        onPublishCompleteOnly={() => { void handleBulkPublishConfirmed(true); }}
      >
        <SyncWarningsBulkLine itemCount={bulkPublishSyncWarningItemCount} />
      </FieldCheckBulkPublishDialog>

      <Dialog open={bulkResetDialogOpen} onClose={() => setBulkResetDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nollställ markerade WordPress-poster</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.25}>
            <Alert severity="warning">
              Detta nollställer Athenas WordPress-state för {selectedBulkAssetIds.length} markerade poster på target {publicationTab ? publicationTypeLabel(publicationTab) : ''}. WordPress-poster eller media ändras inte.
            </Alert>
            <Typography variant="body2" color="text.secondary">
              Skapade WP-bilder, beskärningar och interna matchningar behålls. Poster går tillbaka till Ej vald och kan publiceras igen mot rätt WordPress-miljö.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkResetDialogOpen(false)}>Avbryt</Button>
          <Button
            variant="contained"
            color="error"
            disabled={!!busyAction || !publicationTab || selectedBulkAssetIds.length === 0}
            onClick={async () => {
              setBulkResetDialogOpen(false);
              await handleBulkAction('reset');
            }}
          >
            Nollställ markerade
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={renditionPreviewOpen}
        onClose={closeRenditionPreview}
        maxWidth={false}
        sx={{
          '& .MuiBackdrop-root': {
            bgcolor: 'rgba(0, 0, 0, 0.82)',
          },
        }}
        PaperProps={{
          sx: {
            position: 'relative',
            width: 'auto',
            maxWidth: '95vw',
            maxHeight: '95vh',
            m: 0,
            overflow: 'hidden',
            bgcolor: 'rgba(18, 18, 18, 0.96)',
            boxShadow: 24,
          },
        }}
      >
        <DialogContent sx={{ p: 1.5, overflow: 'auto' }}>
          {selectedRenditionUrl && (
            <Box
              component="img"
              src={selectedRenditionUrl}
              alt={selectedItem?.asset.title ? `${selectedItem.asset.title} WordPress-variant` : 'WordPress-variant'}
              sx={{
                display: 'block',
                width: 'auto',
                height: 'auto',
                maxWidth: 'none',
                maxHeight: 'none',
              }}
            />
          )}
        </DialogContent>
        <IconButton
          onClick={closeRenditionPreview}
          aria-label="Stäng förhandsvisning"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'common.white',
            bgcolor: 'rgba(0, 0, 0, 0.45)',
            '&:hover': {
              bgcolor: 'rgba(0, 0, 0, 0.62)',
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Dialog>
    </Box>
  );
}

export default WordPressPublishingPage;
